// R21 v2 - Naomi Luxe "Plan a Service" clickable mockup
// One file, one route (/design/naomi-luxe), navigate via ?s=<state>
// Static design only. No backend. No wiring. Safe to download as HTML.
//
// Structure:
//   /design/naomi-luxe             -> Master index (grid of every state, grouped by phase)
//   /design/naomi-luxe?s=<state>   -> Single state view (mobile frame)
//
// Sections (phases):
//   A  Dashboard shell
//   B  Plan a Service long-scroll (13 states)
//   C  Preferences slide
//   D  Provider Directory
//   E  Share card / Preference card states
//   F  Waitlist popup
//
// Vocabulary lock: "experiences", "Host", "preferences", "Tags". No em-dashes.

import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Bell, Calendar as CalendarIcon, Camera, Check, CheckCircle2,
  ChevronDown, ChevronRight, ChevronLeft, Circle, Clock, Copy, DollarSign, Download,
  Edit, Eye, EyeOff, FileText, Grid as GridIcon, Heart, Home as HomeIcon, ImageIcon, Info, Lock,
  MapPin, MoreHorizontal, Package, Paintbrush, Palette, Phone, Plus, QrCode, RefreshCw,
  RotateCw, Save, Scissors, Search, Send, Share2, Shirt, Sparkles, Star, Tag as TagIcon,
  Trash2, Upload, User, Users, X, Zap,
} from "lucide-react";

// ---------- design tokens ----------
// Frame token: mobile-width content, seamless edges (no hard lines)
const FRAME = "max-w-[420px] mx-auto bg-white min-h-screen relative pb-24";
const PAGE = "min-h-screen bg-white";
const INK = "#111111";
const ACCENT = "#7c3aed";         // Naomi Luxe accent - deep purple
const ACCENT_SOFT = "#f5f3ff";
const ROSE = "#fecdd3";
const CATEGORY_COLORS = {
  Makeup:  { hex: "#8b5cf6", soft: "#eae2ff", ink: "#4c1d95", Icon: Paintbrush },
  Lashes:  { hex: "#6bc8ba", soft: "#d5f4ee", ink: "#134e4a", Icon: LashSvg },
  Nails:   { hex: "#e08794", soft: "#fbdadd", ink: "#881337", Icon: NailSvg },
  Hair:    { hex: "#d94b7a", soft: "#fce7f0", ink: "#831843", Icon: Scissors },
  Style:   { hex: "#a67c52", soft: "#f5ecd8", ink: "#4c2e12", Icon: HangerSvg },
  Routine: { hex: "#7a7a7a", soft: "#e6e6e6", ink: "#3f3f3f", Icon: RotateCw },
};

// small inline SVGs to match the reference tile art (lash, nail, hanger)
function LashSvg({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 14 C 6 10.5 18 10.5 21.5 14" />
      <path d="M5 12 L 4 8" /><path d="M8 11 L 7.5 6.5" /><path d="M12 10.6 L 12 6" />
      <path d="M16 11 L 16.5 6.5" /><path d="M19 12 L 20 8" />
    </svg>
  );
}
function NailSvg({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 21 L 8 10 C 8 6 10 3 12 3 C 14 3 16 6 16 10 L 16 21 Z" />
      <path d="M8 16 L 16 16" />
    </svg>
  );
}
function HangerSvg({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4 A 2 2 0 1 1 14 6 C 14 7 12 8 12 10" />
      <path d="M3 18 L 12 11 L 21 18 Z" />
    </svg>
  );
}

// ---------- shared primitives ----------
const Chip = ({ children, tone = "stone", size = "sm" }) => {
  const tones = {
    stone: "bg-stone-100 text-stone-700 border-stone-200",
    accent: "bg-purple-50 text-purple-700 border-purple-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
    ink: "bg-stone-900 text-white border-stone-900",
    outline: "bg-white text-stone-700 border-stone-300",
  };
  const sizes = { xs: "text-[9px] px-1.5 py-0.5", sm: "text-[10px] px-2 py-0.5" };
  return <span className={`inline-flex items-center gap-1 rounded-full border ${tones[tone] || tones.stone} ${sizes[size]} font-medium tracking-wider uppercase`}>{children}</span>;
};

const SectionLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-bold px-1">{children}</p>
);

const FieldRow = ({ label, value, action, testid, muted, chip }) => (
  <div className="flex items-center justify-between py-3 border-b border-stone-100" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
      <p className={`text-sm ${muted ? "text-stone-400 italic" : "text-stone-900"} truncate`}>{value}</p>
      {chip && <div className="mt-1">{chip}</div>}
    </div>
    {action && <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 ml-2" />}
  </div>
);

const StepDot = ({ n, active, done, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border ${done ? "bg-stone-900 text-white border-stone-900" : active ? "bg-purple-600 text-white border-purple-600" : "bg-white text-stone-400 border-stone-300"}`}>
      {done ? <Check className="w-3 h-3" /> : n}
    </div>
    <p className={`text-[8px] uppercase tracking-wider ${active ? "text-stone-900 font-bold" : "text-stone-400"}`}>{label}</p>
  </div>
);

const PlanStepper = ({ current }) => {
  const steps = ["Title", "Who", "When", "Where", "Why", "Prefs", "Inspo", "Budget"];
  return (
    <div className="flex items-center justify-between px-4 pt-2 pb-3 overflow-x-auto">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center flex-shrink-0">
          <StepDot n={i + 1} label={s} done={i < current} active={i === current} />
          {i < steps.length - 1 && <div className={`w-3 h-px mx-0.5 ${i < current ? "bg-stone-900" : "bg-stone-200"}`} />}
        </div>
      ))}
    </div>
  );
};

const Btn = ({ children, tone = "ink", full, icon: Icon, size = "md", testid, onClick }) => {
  const tones = {
    ink: "bg-stone-900 text-white hover:bg-black",
    outline: "bg-white text-stone-900 border border-stone-300 hover:bg-stone-50",
    accent: "bg-purple-600 text-white hover:bg-purple-700",
    ghost: "bg-transparent text-stone-700 hover:bg-stone-100",
    danger: "bg-white text-rose-700 border border-rose-200 hover:bg-rose-50",
  };
  const sizes = { sm: "text-xs px-3 py-2", md: "text-sm px-4 py-2.5" };
  return (
    <button onClick={onClick} data-testid={testid} className={`rounded-full font-semibold inline-flex items-center justify-center gap-1.5 ${tones[tone]} ${sizes[size]} ${full ? "w-full" : ""}`}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

// Mobile frame header
const Header = ({ title, back, right, sub }) => (
  <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
    <Link to={back || "/design/naomi-luxe"} className="w-8 h-8 -ml-1 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full">
      <ArrowLeft className="w-4 h-4" />
    </Link>
    <div className="text-center">
      <h1 className="text-[11px] font-bold text-stone-900 uppercase tracking-[0.16em]">{title}</h1>
      {sub && <p className="text-[9px] text-stone-500 mt-0.5">{sub}</p>}
    </div>
    <div className="w-8 h-8 flex items-center justify-center">{right || null}</div>
  </header>
);

// ============================================================================
// SHARED COMPONENT LIBRARY (v1)
// Every popup / segmented button / dropdown / address row must use these.
// See /app/memory/NAOMI_LUXE_COMPONENTS.md for the prop table + rules.
// ============================================================================

// --- Responsive shell for popups. Desktop = dark bg fills screen, mobile-width card centered. ---
const PopupShell = ({ title, titleIcon: TitleIcon, iconColor, onClose = "/design/naomi-luxe?s=dashboard-home", onBack, children, current, footer }) => (
  <div className="min-h-screen bg-stone-950/60 flex items-start sm:items-center justify-center px-3 pt-6 pb-24 sm:py-10" data-testid={`popup-${current}`}>
    <div className="w-full max-w-[480px] bg-white rounded-3xl shadow-2xl relative">
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {TitleIcon && <TitleIcon className="w-4 h-4" style={{ color: iconColor || "#111" }} />}
            <p className="text-base font-bold text-stone-900">{title}</p>
          </div>
          <Link to={onClose} className="w-7 h-7 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="popup-close">
            <X className="w-4 h-4 text-stone-500" />
          </Link>
        </div>
        {onBack && (
          <Link to={onBack} className="text-[11px] text-stone-500 mt-1.5 inline-flex items-center gap-1 hover:text-stone-900" data-testid="popup-back">
            <ChevronLeft className="w-3 h-3" /> Back
          </Link>
        )}
        <div className="mt-4">{children}</div>
        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
    <BottomNav current={current} />
  </div>
);

// --- Section label used above every group (Who is doing it? / Where? / When?) ---
const FieldLabel = ({ children, right }) => (
  <div className="flex items-center justify-between mb-1.5">
    <p className="text-xs font-semibold text-stone-700">{children}</p>
    {right}
  </div>
);
const HintText = ({ children }) => <p className="text-[10px] text-stone-500 mt-1 leading-relaxed">{children}</p>;

// --- Segmented button. Wider padding so labels breathe; fixed height so all children match. ---
const SegButton = ({ children, selected, Icon, onClick, testid, tone = "ink" }) => {
  const on = tone === "ink" ? "bg-stone-900 text-white" : "bg-purple-600 text-white";
  return (
    <button data-testid={testid} onClick={onClick} className={`w-full h-10 min-w-0 rounded-full px-3 text-sm font-semibold inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition ${selected ? on : "bg-white text-stone-700 border border-stone-200 hover:border-stone-400"}`}>
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="truncate">{children}</span>
    </button>
  );
};
const SegRow = ({ children }) => <div className="flex gap-1.5 items-stretch w-full">{children}</div>;

// --- Text input (used for city, phone, address, all free-form). ---
const TextInput = ({ value, placeholder, testid, defaultValue, icon: Icon }) => (
  <div className="mt-2 rounded-xl border border-stone-300 flex items-center gap-2 px-3 py-2 focus-within:border-stone-900">
    {Icon && <Icon className="w-3.5 h-3.5 text-stone-500 shrink-0" />}
    <input defaultValue={defaultValue} value={value} placeholder={placeholder} className="flex-1 bg-transparent text-xs focus:outline-none" data-testid={testid} />
  </div>
);

// --- Search box row inside a dropdown. Consistent shape. ---
const SearchRow = ({ placeholder, testid }) => (
  <div className="rounded-xl border border-stone-200 bg-stone-50 flex items-center gap-2 px-3 py-2">
    <Search className="w-3.5 h-3.5 text-stone-500 shrink-0" />
    <input placeholder={placeholder} className="flex-1 bg-transparent text-xs focus:outline-none" data-testid={testid} />
  </div>
);

// --- Provider row inside the dropdown. Selectable pill card. ---
const ProviderChip = ({ name, selected, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`w-full rounded-xl border px-3 py-2.5 flex items-center gap-2.5 transition ${selected ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 bg-white text-stone-800 hover:border-stone-400"}`}>
    <div className={`w-4 h-4 rounded-full border-2 ${selected ? "border-white bg-white" : "border-stone-300"}`}>
      {selected && <div className="w-full h-full rounded-full bg-stone-900 scale-50" />}
    </div>
    <span className="text-xs font-semibold flex-1 text-left">{name}</span>
  </button>
);

// --- Read-only address row that renders under the "Where" segment once known. ---
const AddressRow = ({ label, address, testid }) => (
  <div className="mt-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-1"><MapPin className="w-3.5 h-3.5 text-stone-500" /><p className="text-xs font-semibold text-stone-700">{label}</p></div>
    <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-800">{address}</div>
  </div>
);

// --- Consistent small "up arrow" share icon. Only used on Naomi's dark card, provider detail, share card. ---
const ShareArrow = ({ size = 14, dark = true, testid }) => (
  <button data-testid={testid || "share-arrow"} className={`w-7 h-7 rounded-full flex items-center justify-center ${dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-stone-100 text-stone-800 hover:bg-stone-200"}`}>
    <Upload style={{ width: size, height: size }} />
  </button>
);

// --- Big shortcut row used for Preferences / Inspo entry inside Plan Makeup. Shows filled state chips when data exists. ---
const ShortcutRow = ({ to, icon: Icon, label, testid, filled }) => (
  <Link to={to} data-testid={testid} className="block rounded-2xl border border-stone-300 hover:border-stone-900 bg-white transition">
    <div className="px-3 py-2.5 flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-stone-600 shrink-0" />
      <span className="flex-1 text-xs font-semibold text-stone-800">{label}</span>
      <ChevronRight className="w-3.5 h-3.5 text-stone-400" />
    </div>
    {filled && (
      <div className="px-3 pb-3 pt-0 border-t border-stone-100 flex flex-wrap gap-1.5 items-center">
        {filled}
      </div>
    )}
  </Link>
);


// ---------- STATE REGISTRY (V1) ----------
const REGISTRY = [
  { phase: "A", group: "Dashboard", id: "dashboard-home", label: "Dashboard - home" },

  { phase: "B", group: "Plan a Service", id: "ps-category", label: "1. What type of experience?" },
  { phase: "B", group: "Plan a Service", id: "ps-makeup-modal", label: "2. Plan Makeup - default TBD" },
  { phase: "B", group: "Plan a Service", id: "ps-makeup-providers-open", label: "3. Providers dropdown open" },
  { phase: "B", group: "Plan a Service", id: "ps-makeup-provider-selected", label: "4. ShelseaLuxe selected (address)" },
  { phase: "B", group: "Plan a Service", id: "ps-makeup-pick-datetime", label: "5. Pick date & time inline" },

  { phase: "C", group: "Popups", id: "preferences", label: "Edit preferences (long scroll)" },
  { phase: "C", group: "Popups", id: "inspo", label: "Add inspo" },
  { phase: "C", group: "Popups", id: "add-provider", label: "Add new provider" },

  { phase: "D", group: "Preference Card", id: "card-owner", label: "Snapshot page" },
  { phase: "D", group: "Preference Card", id: "card-details", label: "Full details (eye icon)" },
  { phase: "D", group: "Preference Card", id: "card-public", label: "Public - shared with you" },
  { phase: "D", group: "Preference Card", id: "card-visibility", label: "Visibility settings" },

  { phase: "Z", group: "Admin", id: "admin-control", label: "Control Center (single scroll)" },
];

// ---------- helpers to navigate between states ----------
function useStateNav(current) {
  const nav = useNavigate();
  const idx = REGISTRY.findIndex(x => x.id === current);
  const prev = idx > 0 ? REGISTRY[idx - 1] : null;
  const next = idx < REGISTRY.length - 1 ? REGISTRY[idx + 1] : null;
  return {
    prev, next,
    go: (id) => nav(`/design/naomi-luxe?s=${id}`),
    toIndex: () => nav(`/design/naomi-luxe`),
  };
}

// Bottom nav bar - full width on desktop, always at bottom
const BottomNav = ({ current }) => {
  const { prev, next } = useStateNav(current);
  const meta = REGISTRY.find(r => r.id === current);
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-stone-900 text-white shadow-2xl">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-2">
        <Link to="/design/naomi-luxe" className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold hover:text-purple-300" data-testid="nav-index">
          <GridIcon className="w-3 h-3" /> Index
        </Link>
        <div className="text-center flex-1 min-w-0">
          <p className="text-[9px] text-stone-400 uppercase tracking-wider truncate">{meta?.group}</p>
          <p className="text-[10px] font-bold truncate">{meta?.label}</p>
        </div>
        <div className="flex items-center gap-1">
          {prev && <Link to={`/design/naomi-luxe?s=${prev.id}`} className="w-7 h-7 rounded-full bg-stone-700 hover:bg-stone-600 flex items-center justify-center" data-testid="nav-prev"><ChevronLeft className="w-4 h-4" /></Link>}
          {next && <Link to={`/design/naomi-luxe?s=${next.id}`} className="w-7 h-7 rounded-full bg-purple-600 hover:bg-purple-500 flex items-center justify-center" data-testid="nav-next"><ChevronRight className="w-4 h-4" /></Link>}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// INDEX (Home) - clickable grid of every state, grouped by phase
// ============================================================================
function NaomiLuxeIndex() {
  const groups = [...new Set(REGISTRY.map(r => `${r.phase}::${r.group}`))];
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-3xl mx-auto px-5 py-8">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-stone-900 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <Chip tone="ink">R21 v2 - Naomi Luxe (Shelsea)</Chip>
            <Chip tone="outline">Static prototype</Chip>
          </div>
          <h1 className="text-3xl font-black text-stone-900 tracking-tight leading-tight">Plan a Service <span className="text-stone-400 font-light">- clickable mockup</span></h1>
          <p className="text-sm text-stone-600 mt-3 max-w-lg leading-relaxed">
            End-to-end static flow: Dashboard, Plan a Service long-scroll, Preferences (one big scroll), Provider Directory, Share card states, and Waitlist popup. Every tile below opens the mobile mockup. Use the black bottom bar to jump between states, or return here anytime.
          </p>
          <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-3 flex items-center gap-2 max-w-lg">
            <QrCode className="w-4 h-4 text-stone-700 shrink-0" />
            <code className="text-[11px] text-stone-800 font-mono flex-1 truncate">{typeof window !== "undefined" ? window.location.origin : ""}/design/naomi-luxe</code>
            <button onClick={() => { if (typeof navigator !== "undefined" && navigator.clipboard) { navigator.clipboard.writeText(`${window.location.origin}/design/naomi-luxe`); }}} className="text-[10px] font-bold uppercase text-white bg-stone-900 rounded-full px-3 py-1.5">Copy link</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/design/naomi-luxe?s=dashboard-home" className="text-xs px-3 py-2 rounded-full bg-stone-900 text-white font-semibold hover:bg-black inline-flex items-center gap-1.5" data-testid="cta-start">
              Start walkthrough <ArrowRight className="w-3 h-3" />
            </Link>
            <a href="/design/naomi-luxe" onClick={(e) => { e.preventDefault(); window.print(); }} className="text-xs px-3 py-2 rounded-full bg-white border border-stone-300 text-stone-700 font-semibold hover:bg-stone-50 inline-flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Save as HTML
            </a>
            <Link to="/me" className="text-xs px-3 py-2 rounded-full bg-white border border-stone-300 text-stone-700 font-semibold hover:bg-stone-50 inline-flex items-center gap-1.5">
              <HomeIcon className="w-3 h-3" /> Live app
            </Link>
          </div>
        </div>

        {/* Airtable CSV download bundle */}
        <section className="mb-8 rounded-3xl border-2 border-stone-900 bg-gradient-to-br from-white to-stone-50 p-5" data-testid="csv-bundle">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-stone-900 flex items-center justify-center">
                  <Download className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-stone-900">Airtable CSV bundle - v2</span>
              </div>
              <h2 className="text-xl font-black text-stone-900 leading-tight">Phygital Beauty · Airtable import bundle</h2>
              <p className="text-xs text-stone-600 mt-1 max-w-lg leading-relaxed">
                Eight clickable downloads below. Each maps 1:1 to a Naomi Luxe Airtable table. Import in the listed order, then set column types per the README. Delete or set Enabled=FALSE on any row in Airtable and it disappears from the app within 5 minutes. Cohorts and Feature Flags let you release features to specific users only. Theme lets you edit colors and typography without a deploy.
              </p>
            </div>
            <a href="/naomi-luxe-csvs/README.md" download className="text-[10px] font-bold uppercase tracking-wider text-stone-700 bg-white border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 inline-flex items-center gap-1 shrink-0" data-testid="csv-readme">
              <FileText className="w-3 h-3" /> README
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {[
              { file: "naomi_categories.csv", label: "Categories", rows: 6, order: 1 },
              { file: "naomi_occasions.csv", label: "Occasions", rows: 6, order: 2 },
              { file: "naomi_occasion_categories.csv", label: "Occasion Categories (link)", rows: 21, order: 3 },
              { file: "naomi_pref_schema.csv", label: "Preferences Schema", rows: 23, order: 4 },
              { file: "naomi_pref_options.csv", label: "Preferences Options", rows: 55, order: 5 },
              { file: "naomi_copy.csv", label: "UI Copy", rows: 44, order: 6 },
              { file: "naomi_features.csv", label: "Feature Flags", rows: 25, order: 7 },
              { file: "naomi_audience_cohorts.csv", label: "Audience Cohorts", rows: 6, order: 8 },
              { file: "naomi_theme.csv", label: "Theme Tokens", rows: 17, order: 9 },
            ].map(c => (
              <a
                key={c.file}
                href={`/naomi-luxe-csvs/${c.file}`}
                download
                className="group rounded-xl border border-stone-200 bg-white hover:border-stone-900 hover:shadow transition-all p-3 flex items-center gap-3"
                data-testid={`csv-download-${c.file.replace(".csv", "")}`}
              >
                <div className="w-9 h-9 rounded-md bg-stone-100 group-hover:bg-stone-900 flex items-center justify-center transition shrink-0">
                  <FileText className="w-4 h-4 text-stone-700 group-hover:text-white transition" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-stone-900 truncate">{c.order}. {c.label}</p>
                  <p className="text-[10px] text-stone-500">{c.file} - {c.rows} rows</p>
                </div>
                <Download className="w-3.5 h-3.5 text-stone-400 group-hover:text-stone-900 shrink-0" />
              </a>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] text-stone-500">
            <Chip tone="outline" size="xs">203 total rows</Chip>
            <Chip tone="outline" size="xs">9 tables</Chip>
            <Chip tone="outline" size="xs">Zero em-dashes</Chip>
            <Chip tone="outline" size="xs">Base appiOqLJaImb5Cg80</Chip>
            <Chip tone="outline" size="xs">Cohort + flag gated</Chip>
          </div>
        </section>

        {/* Phase groups */}
        {groups.map(g => {
          const [phase, group] = g.split("::");
          const items = REGISTRY.filter(r => r.phase === phase && r.group === group);
          return (
            <section key={g} className="mb-8">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-xs font-black text-stone-900 tracking-widest">PHASE {phase}</span>
                <h2 className="text-lg font-bold text-stone-900">{group}</h2>
                <span className="text-[10px] text-stone-400">{items.length} states</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {items.map(item => (
                  <Link key={item.id} to={`/design/naomi-luxe?s=${item.id}`}
                    className="group rounded-2xl border border-stone-200 bg-white hover:border-stone-900 hover:shadow-lg transition-all overflow-hidden"
                    data-testid={`index-tile-${item.id}`}>
                    <div className="aspect-[3/4] bg-gradient-to-br from-stone-50 to-stone-100 relative overflow-hidden">
                      <MiniPreview id={item.id} />
                      <div className="absolute top-2 left-2">
                        <Chip tone="outline" size="xs">{item.id}</Chip>
                      </div>
                    </div>
                    <div className="p-2.5 border-t border-stone-100 flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-stone-900 truncate">{item.label}</p>
                      <ArrowRight className="w-3 h-3 text-stone-400 group-hover:text-stone-900 shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        <footer className="mt-12 pt-6 border-t border-stone-200 text-[11px] text-stone-500 leading-relaxed">
          <p><b className="text-stone-700">Design-only sandbox.</b> None of these buttons touch the live database or Acuity. Approved states graduate into functional code in a separate PR.</p>
          <p className="mt-2">Route: <code className="bg-stone-100 px-1.5 py-0.5 rounded">/design/naomi-luxe?s=&lt;state&gt;</code></p>
        </footer>
      </div>
    </div>
  );
}

// Tiny SVG-style abstract preview of each state so the grid feels alive
const MiniPreview = ({ id }) => {
  const kind = id.split("-")[0];
  const tone = id.startsWith("card") ? "purple" : id.startsWith("prefs") ? "rose" : id.startsWith("pd") ? "emerald" : id.startsWith("ps") ? "amber" : "stone";
  const tones = {
    purple: "from-purple-100 to-purple-50",
    rose: "from-rose-100 to-rose-50",
    emerald: "from-emerald-100 to-emerald-50",
    amber: "from-amber-100 to-amber-50",
    stone: "from-stone-200 to-stone-100",
  };
  return (
    <div className={`absolute inset-0 bg-gradient-to-br ${tones[tone]}`}>
      <div className="absolute inset-3 rounded-lg bg-white/70 backdrop-blur border border-white flex flex-col p-2 gap-1">
        <div className="h-1.5 w-8 bg-stone-300 rounded-full" />
        <div className="h-2 w-full bg-stone-200 rounded-full" />
        <div className="h-2 w-3/4 bg-stone-200 rounded-full" />
        <div className="flex-1 mt-1 flex items-end gap-1">
          <div className="h-4 w-4 bg-stone-900 rounded" />
          <div className="h-3 w-6 bg-stone-300 rounded" />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PHASE A - Dashboard
// ============================================================================
const PILOT_CATEGORIES = [
  { name: "Makeup",  desc: "Full glam, bridal, everyday" },
  { name: "Lashes",  desc: "Sets, fills, natural to volume" },
  { name: "Nails",   desc: "Manicures, sets, art" },
  { name: "Hair",    desc: "Cut, color, style, extensions" },
  { name: "Style",   desc: "Outfits, wardrobe planning" },
  { name: "Routine", desc: "Daily skincare + upkeep" },
];

// A soft pastel category tile that mirrors the reference art (Routine / Hair / Lash / Nail / Makeup / Style)
const CategoryTile = ({ name, size = "md" }) => {
  const c = CATEGORY_COLORS[name];
  const Icon = c?.Icon;
  const sizes = { sm: "w-9 h-9 rounded-xl", md: "w-14 h-14 rounded-2xl", lg: "w-16 h-16 rounded-2xl" };
  const iconSize = { sm: "w-4 h-4", md: "w-6 h-6", lg: "w-7 h-7" };
  return (
    <div className={`${sizes[size]} flex items-center justify-center`} style={{ backgroundColor: c?.soft, color: c?.hex }}>
      {Icon ? <Icon className={iconSize[size]} /> : <span className="font-black text-lg" style={{ color: c?.hex }}>{name[0]}</span>}
    </div>
  );
};

function DashboardHome() {
  return (
    <div className={PAGE}>
    <div className={FRAME}>
      {/* phygital header with bell + avatar dropdown */}
      <header className="sticky top-0 z-30 bg-white border-b border-stone-200 px-4 h-14 flex items-center justify-between">
        <Link to="/design/naomi-luxe" className="flex items-center gap-1.5" data-testid="header-logo">
          <div className="w-5 h-5 rounded-md bg-stone-900 flex items-center justify-center">
            <User className="w-3 h-3 text-white" />
          </div>
          <span className="text-sm font-bold text-stone-900 lowercase tracking-tight">phygital</span>
        </Link>
        <div className="flex items-center gap-3">
          <Bell className="w-4 h-4 text-stone-500" />
          <button className="flex items-center gap-0.5">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-200 via-amber-100 to-purple-200 border border-stone-200" />
            <ChevronDown className="w-3 h-3 text-stone-500" />
          </button>
        </div>
      </header>

      {/* Tabs - pill capsule with icons */}
      <div className="px-4 pt-3">
        <div className="bg-stone-100 p-1 rounded-full flex" data-testid="main-tabs">
          {[
            { l: "Dashboard", Ic: HomeIcon, on: true },
            { l: "Preferences", Ic: Heart },
            { l: "Inspo", Ic: Camera },
            { l: "Experiences", Ic: Sparkles },
          ].map(t => (
            <button key={t.l} className={`flex-1 text-[10px] py-1.5 rounded-full inline-flex items-center justify-center gap-1 ${t.on ? "bg-white shadow text-stone-900 font-semibold" : "text-stone-500"}`}>
              <t.Ic className="w-3 h-3" /> {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Naomi Thomas dark hero card */}
      <div className="px-4 pt-4">
        <div className="rounded-2xl bg-stone-900 text-white p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 to-purple-300 border-2 border-white/20 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight">Naomi Thomas</p>
            <p className="text-[10px] text-white/60 tracking-widest uppercase mt-0.5">PHYG-TECHBAENAE</p>
          </div>
          <button className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20" data-testid="share-profile">
            <Upload className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Upcoming */}
      <div className="px-4 pt-5 space-y-3">
        <p className="text-sm font-bold text-stone-900">Upcoming</p>

        {/* Plan a Service - light purple */}
        <Link to="/design/naomi-luxe?s=ps-category" className="block rounded-2xl p-4 relative hover:scale-[1.01] transition" style={{ backgroundColor: "#efe8ff" }} data-testid="cta-plan-service">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-stone-900">Plan a Service</p>
              <p className="text-[11px] text-stone-500 mt-0.5">Hair, Makeup, Nails, Lashes</p>
            </div>
          </div>
        </Link>

        {/* Plan around an Occasion - neutral */}
        <Link to="/design/naomi-luxe?s=ps-category" className="block rounded-2xl border border-stone-200 bg-white p-4 hover:border-stone-900 transition" data-testid="cta-plan-occasion">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center">
              <Zap className="w-4 h-4 text-stone-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-stone-900">Plan around an Occasion</p>
              <p className="text-[11px] text-stone-500 mt-0.5">Wedding, Birthday, Photoshoot</p>
            </div>
          </div>
        </Link>
      </div>

      <BottomNav current="dashboard-home" />
    </div>
    </div>
  );
}


// ============================================================================
// PHASE B - Plan a Service long-scroll
// ============================================================================

// wrapper shell for every plan-service state
function PsCategory() {
  return (
    <PopupShell current="ps-category" title="What type of experience?" titleIcon={Sparkles} onClose="/design/naomi-luxe?s=dashboard-home">
      <div className="grid grid-cols-2 gap-2.5">
        {PILOT_CATEGORIES.map(c => (
          <Link key={c.name} to={c.name === "Makeup" ? "/design/naomi-luxe?s=ps-makeup-modal" : "/design/naomi-luxe?s=ps-makeup-modal"} data-testid={`cat-${c.name}`}
            className="rounded-2xl p-4 hover:scale-[1.02] transition"
            style={{ backgroundColor: CATEGORY_COLORS[c.name].soft }}>
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mb-3">
              {(() => { const Ic = CATEGORY_COLORS[c.name].Icon; return <Ic className="w-4 h-4" style={{ color: CATEGORY_COLORS[c.name].hex }} />; })()}
            </div>
            <p className="text-sm font-bold text-stone-900">{c.name}</p>
          </Link>
        ))}
      </div>
    </PopupShell>
  );
}


// ---------- Plan Your Makeup Experience - unified modal (V1) ----------
// Every reveal / dropdown / address happens INLINE inside this modal.
// The 4 registry entries below correspond to variants:
//   tbd                  -> default state, TBD everywhere
//   providers-open       -> Who dropdown expanded, search + ShelseaLuxe + add-new
//   provider-selected    -> ShelseaLuxe selected, "At provider" address surfaces
//   pick-datetime        -> Date & time inline picker

const MOCK_PROVIDERS = [
  { id: "shelsea", name: "ShelseaLuxe", cat: "Makeup", address: "1947 N. Cahuenga Blvd 102, Los Angeles, CA 90068", phone: "(213) 555 0180" },
];

function PsMakeupModal({ variant = "tbd" }) {
  const providersOpen  = variant === "providers-open";
  const providerPicked = variant === "provider-selected";
  const pickingDT      = variant === "pick-datetime";

  const whoSelected     = providerPicked ? "shelsea" : "tbd";
  const whereSelected   = providerPicked ? "at-provider" : "tbd-city";
  const whenSelected    = pickingDT ? "pick" : "tbd";
  const providerCount   = MOCK_PROVIDERS.length;

  const stateId = variant === "tbd" ? "ps-makeup-modal"
    : variant === "providers-open" ? "ps-makeup-providers-open"
    : variant === "provider-selected" ? "ps-makeup-provider-selected"
    : "ps-makeup-pick-datetime";

  return (
    <PopupShell
      current={stateId}
      title="Plan Your Makeup Experience"
      titleIcon={Paintbrush}
      iconColor="#8b5cf6"
      onClose="/design/naomi-luxe?s=dashboard-home"
      onBack="/design/naomi-luxe?s=ps-category"
      footer={
        <div className="flex gap-2 items-center">
          <Link to="/design/naomi-luxe?s=card-details" className="w-10 h-10 rounded-xl border border-stone-300 flex items-center justify-center hover:border-stone-900" data-testid="preview-card"><Eye className="w-4 h-4 text-stone-700" /></Link>
          <Link to="/design/naomi-luxe?s=card-owner" className="flex-1"><Btn tone="ink" full size="md" icon={Check} testid="save-experience">Save plan</Btn></Link>
        </div>
      }
    >
      {/* Category chip - Makeup */}
      <div className="rounded-xl p-3 flex items-center gap-2 mb-4" style={{ backgroundColor: CATEGORY_COLORS.Makeup.soft }}>
        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center"><Paintbrush className="w-3 h-3 text-purple-600" /></div>
        <span className="text-sm font-semibold text-stone-900 flex-1">Makeup</span>
        <Link to="/design/naomi-luxe?s=ps-category" className="text-stone-500 hover:text-stone-900"><Edit className="w-3.5 h-3.5" /></Link>
      </div>

      {/* WHO */}
      <div className="mb-5">
        <FieldLabel right={
          <Link to={`/design/naomi-luxe?s=${providersOpen || providerPicked ? "ps-makeup-modal" : "ps-makeup-providers-open"}`} className="text-[10px] text-purple-700 font-semibold inline-flex items-center gap-1" data-testid="my-providers-btn">
            <User className="w-3 h-3" /> My Providers ({providerCount}) <ChevronDown className={`w-3 h-3 transition ${providersOpen ? "rotate-180" : ""}`} />
          </Link>
        }>Who is doing it?</FieldLabel>
        <SegRow>
          <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="flex-1"><SegButton Icon={User} testid="who-diy">DIY</SegButton></Link>
          <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="flex-1"><SegButton Icon={Clock} selected={whoSelected === "tbd"} testid="who-tbd">TBD</SegButton></Link>
          {providerPicked ? (
            <button
              data-testid="who-shelsea"
              className="flex-1 h-10 min-w-0 rounded-full bg-stone-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 px-4 whitespace-nowrap"
            >
              <span className="w-3 h-3 rounded-full bg-white inline-block shrink-0" />
              <span className="truncate">{MOCK_PROVIDERS[0].name}</span>
            </button>
          ) : (
            <Link to="/design/naomi-luxe?s=ps-makeup-providers-open" className="flex-1"><SegButton Icon={Plus} testid="who-add">Add</SegButton></Link>
          )}
        </SegRow>

        {/* Providers dropdown - only when providers-open */}
        {providersOpen && (
          <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm space-y-2" data-testid="providers-dropdown">
            <SearchRow placeholder="Search providers..." testid="providers-search" />
            <div className="space-y-1.5">
              {MOCK_PROVIDERS.map(p => (
                <Link key={p.id} to="/design/naomi-luxe?s=ps-makeup-provider-selected">
                  <ProviderChip name={p.name} testid={`provider-${p.id}`} />
                </Link>
              ))}
            </div>
            <Link to="/design/naomi-luxe?s=add-provider" className="w-full rounded-xl border border-dashed border-stone-300 px-3 py-2.5 flex items-center justify-center gap-2 text-xs font-semibold text-stone-600 hover:border-stone-900" data-testid="add-new-provider">
              <Plus className="w-3.5 h-3.5" /> Add new provider
            </Link>
          </div>
        )}
      </div>

      {/* WHERE */}
      <div className="mb-5">
        <FieldLabel>Where?</FieldLabel>
        <SegRow>
          <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="flex-1"><SegButton selected={whereSelected === "tbd-city"} testid="where-tbd">TBD - just city</SegButton></Link>
          <Link to="/design/naomi-luxe?s=ps-makeup-provider-selected" className="flex-1"><SegButton selected={whereSelected === "at-provider"} testid="where-provider">At provider</SegButton></Link>
          <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="flex-1"><SegButton testid="where-travel">Travel to me</SegButton></Link>
        </SegRow>

        {/* Conditional address / city inputs */}
        {whereSelected === "tbd-city" && (
          <>
            <FieldLabel><span className="inline-flex items-center gap-1 text-stone-700 font-semibold text-xs mt-3"><MapPin className="w-3.5 h-3.5 text-stone-500" /> City, State, Country</span></FieldLabel>
            <HintText>Just the city, not a full address. We'll narrow it down with your host.</HintText>
            <TextInput defaultValue="Los Angeles, CA" testid="where-city" />
          </>
        )}
        {whereSelected === "at-provider" && (
          <AddressRow label="Provider's Address" address={MOCK_PROVIDERS[0].address} testid="where-provider-address" />
        )}
      </div>

      {/* WHEN - simple 2-button seg */}
      <div className="mb-4">
        <FieldLabel>When?</FieldLabel>
        <SegRow>
          <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="flex-1"><SegButton Icon={Clock} selected={whenSelected === "tbd"} testid="when-tbd">Date TBD</SegButton></Link>
          <Link to="/design/naomi-luxe?s=ps-makeup-pick-datetime" className="flex-1"><SegButton Icon={CalendarIcon} selected={whenSelected === "pick"} testid="when-pick">Pick date & time</SegButton></Link>
        </SegRow>
        {whenSelected === "pick" && <InlineDateTime />}
      </div>

      {/* OCCASION */}
      <div className="mb-5">
        <FieldLabel><span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-stone-500" /> What's the occasion? (optional)</span></FieldLabel>
        <button className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm text-stone-500 flex items-center justify-between hover:border-stone-500" data-testid="occasion-dropdown">
          <span>Search or pick an occasion...</span>
          <ChevronDown className="w-4 h-4 text-stone-400" />
        </button>
      </div>

      {/* WHAT IS THE LOOK? */}
      <div className="mb-5">
        <h3 className="text-sm font-bold text-stone-900 mb-3">What is the look?</h3>

        {/* Service pills */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-stone-700 mb-1.5">Service</p>
          <div className="flex flex-wrap gap-1.5">
            {["Full Glam", "Soft Glam", "Touch-Up", "Bridal Trial"].map(s => (
              <button key={s} className="text-[11px] px-3 py-1 rounded-full border border-stone-300 bg-white text-stone-700 hover:border-stone-900" data-testid={`service-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}>{s}</button>
            ))}
            <button className="text-[11px] px-3 py-1 rounded-full border border-dashed border-stone-300 text-stone-600 inline-flex items-center gap-1" data-testid="add-service"><Plus className="w-3 h-3" /> Add new service</button>
          </div>
        </div>

        {/* Preferences + Inspiration cards side by side */}
        <div className="grid grid-cols-2 gap-2">
          {/* Preferences card */}
          <div className="rounded-2xl border border-stone-200 bg-white p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-purple-600" />
              <p className="text-xs font-bold text-stone-900">Preferences</p>
            </div>
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-xs font-semibold text-stone-500 rounded-full border border-stone-200 px-2.5 py-0.5">(0)</span>
              <Link to="/design/naomi-luxe?s=preferences" className="ml-auto text-[10px] font-bold text-stone-700 uppercase tracking-wider inline-flex items-center gap-1 hover:text-stone-900" data-testid="open-prefs"><Edit className="w-3 h-3" /> Edit preferences</Link>
            </div>
            <div className="text-[11px] text-stone-500 space-y-1">
              <div className="flex items-center gap-1.5"><span className="text-stone-700 font-semibold">Allergies</span><Chip tone="outline" size="xs">None</Chip></div>
              <div className="flex items-center gap-1.5"><span className="text-stone-700 font-semibold">Look</span><Chip tone="outline" size="xs">Full Glam</Chip></div>
            </div>
          </div>

          {/* Inspiration card */}
          <div className="rounded-2xl border border-stone-200 bg-white p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Camera className="w-3.5 h-3.5 text-stone-600" />
              <p className="text-xs font-bold text-stone-900">Inspiration -- upload here</p>
            </div>
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-xs font-semibold text-stone-500 rounded-full border border-stone-200 px-2.5 py-0.5">(0)</span>
              <Link to="/design/naomi-luxe?s=inspo" className="ml-auto text-[10px] font-bold text-stone-700 uppercase tracking-wider inline-flex items-center gap-1 hover:text-stone-900" data-testid="open-inspo"><Edit className="w-3 h-3" /> Edit inspo</Link>
            </div>
            <div className="rounded-lg border border-dashed border-stone-300 h-16 flex items-center justify-center">
              <Upload className="w-4 h-4 text-stone-400" />
            </div>
          </div>
        </div>
      </div>

      {/* BUDGET */}
      <div className="mb-2">
        <p className="text-sm font-bold text-stone-900 mb-2">Budget (optional)</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[11px] text-stone-600 mb-1">Service ($)</p>
            <div className="rounded-xl border border-stone-300 flex items-center px-3 py-2">
              <DollarSign className="w-3.5 h-3.5 text-stone-500" />
              <input defaultValue="0" className="flex-1 bg-transparent text-sm focus:outline-none ml-1" data-testid="budget-service" />
            </div>
          </div>
          <div>
            <p className="text-[11px] text-stone-600 mb-1">Products ($)</p>
            <div className="rounded-xl border border-stone-300 flex items-center px-3 py-2">
              <DollarSign className="w-3.5 h-3.5 text-stone-500" />
              <input defaultValue="0" className="flex-1 bg-transparent text-sm focus:outline-none ml-1" data-testid="budget-products" />
            </div>
          </div>
        </div>
      </div>
    </PopupShell>
  );
}

// Inline date + time picker with Flexibility + alt slots
const InlineDateTime = () => (
  <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-3 space-y-3" data-testid="inline-datetime">
    {/* Calendar */}
    <div>
      <div className="flex items-center justify-between mb-2">
        <button className="w-6 h-6 rounded-full bg-stone-100"><ChevronLeft className="w-3 h-3 mx-auto" /></button>
        <p className="text-xs font-bold uppercase tracking-wider">June 2026</p>
        <button className="w-6 h-6 rounded-full bg-stone-100"><ChevronRight className="w-3 h-3 mx-auto" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d, i) => <p key={i} className="text-[9px] text-stone-400 py-1 font-semibold">{d}</p>)}
        {Array.from({ length: 30 }).map((_, i) => {
          const day = i + 1;
          const locked = day === 15;
          const flex = [9].includes(day);
          return <div key={i} className={`aspect-square flex items-center justify-center text-[11px] rounded-md ${locked ? "bg-stone-900 text-white font-bold" : flex ? "bg-stone-100 text-stone-900" : "text-stone-700 hover:bg-stone-50"}`}>{day}</div>;
        })}
      </div>
    </div>

    {/* Time chips grid */}
    <div className="grid grid-cols-4 gap-1">
      {["8:00 AM","8:15 AM","8:30 AM","8:45 AM","9:00 AM","9:15 AM","9:30 AM","9:45 AM","10:00 AM","10:15 AM","10:30 AM","10:45 AM","11:00 AM","11:15 AM","11:30 AM","11:45 AM","12:00 PM","12:15 PM","12:30 PM","12:45 PM"].map((t, i) => (
        <button key={t} className={`text-[10px] px-1.5 py-1 rounded-full border ${t === "10:30 AM" ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 text-stone-700"}`}>{t}</button>
      ))}
    </div>

    {/* Flexibility */}
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-stone-500 font-bold shrink-0">Flexibility</span>
      <div className="flex gap-1.5 flex-wrap">
        {["Locked", "Flex time", "Flex date+time"].map((f, i) => (
          <button key={f} className={`text-[10px] px-2.5 py-1 rounded-full border ${i === 1 ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300"}`} data-testid={`flex-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}>{f}</button>
        ))}
      </div>
    </div>

    {/* Alt slots */}
    <div className="space-y-1.5">
      {[
        { n: "alt 1", date: "Jun 15", time: "14:00" },
        { n: "alt 2", date: "Jun 15", time: "16:30" },
      ].map(a => (
        <div key={a.n} className="rounded-xl border border-stone-200 bg-white px-3 py-2 flex items-center gap-2" data-testid={`alt-${a.n.replace(" ", "-")}`}>
          <span className="text-[10px] uppercase tracking-wider text-stone-500 font-bold w-10">{a.n}</span>
          <span className="text-xs font-semibold text-stone-800 flex-1">{a.date}</span>
          <span className="text-xs font-mono text-stone-800">{a.time}</span>
          <button className="text-stone-400 hover:text-stone-900"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button className="w-full rounded-xl border border-dashed border-stone-300 text-xs font-semibold text-stone-600 py-2 inline-flex items-center justify-center gap-1 hover:border-stone-900" data-testid="add-alt-time">
        <Plus className="w-3 h-3" /> Add alternate date+time (2/5)
      </button>
    </div>
  </div>
);



function PsInspo() {
  return (
    <PopupShell
      current="inspo"
      title="Add inspo"
      titleIcon={Camera}
      onClose="/design/naomi-luxe?s=dashboard-home"
      onBack="/design/naomi-luxe?s=ps-makeup-modal"
      footer={<Link to="/design/naomi-luxe?s=ps-makeup-modal"><Btn tone="ink" full icon={Check} size="sm" testid="inspo-done">Done - 3 selected</Btn></Link>}
    >
      <div className="bg-stone-100 p-1 rounded-full flex mb-3">
        <button className="flex-1 text-xs py-2 rounded-full bg-white shadow font-semibold text-stone-900 inline-flex items-center justify-center gap-1.5" data-testid="inspo-tab-upload"><Upload className="w-3.5 h-3.5" /> Upload</button>
        <button className="flex-1 text-xs py-2 rounded-full text-stone-500 inline-flex items-center justify-center gap-1.5" data-testid="inspo-tab-library"><ImageIcon className="w-3.5 h-3.5" /> My library (12)</button>
      </div>

      {/* Compact upload zone */}
        <button className="w-full rounded-2xl border-2 border-dashed border-stone-300 bg-stone-50 p-4 flex items-center gap-3 hover:border-stone-900" data-testid="inspo-upload-zone">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center"><Upload className="w-4 h-4 text-stone-700" /></div>
          <div className="text-left flex-1">
            <p className="text-xs font-bold text-stone-900">Upload from device</p>
            <p className="text-[10px] text-stone-500 mt-0.5">Up to 12 images per experience</p>
          </div>
          <Plus className="w-4 h-4 text-stone-500" />
        </button>

      {/* Uploaded images grid */}
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mt-4 mb-2">Uploaded for this look</p>
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map(i => (
          <div key={i} className="aspect-square rounded-xl relative overflow-hidden ring-2 ring-stone-900" data-testid={`inspo-image-${i}`}>
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${["#fecdd3", "#f3e8ff", "#fef3c7"][i]}, #fff)` }} />
            <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-stone-900 text-white flex items-center justify-center"><Check className="w-3 h-3" /></div>
            <div className="absolute bottom-1 left-1"><Chip tone="ink" size="xs">{["inspo","before","final"][i]}</Chip></div>
          </div>
        ))}
        {[3, 4, 5].map(i => (
          <div key={i} className="aspect-square rounded-xl relative overflow-hidden" data-testid={`inspo-image-${i}`}>
            <div className="absolute inset-0 bg-stone-100" />
          </div>
        ))}
      </div>
    </PopupShell>
  );
}

// --- Preference Card (owner-facing) - simple snapshot page ---
function CardOwner() {
  return (
    <div className={PAGE}>
    <div className={FRAME + " bg-stone-100"}>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
        <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="w-8 h-8 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full" data-testid="card-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Preference card</p>
        <Link to="/design/naomi-luxe?s=card-details" className="w-8 h-8 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full" data-testid="card-eye">
          <MoreHorizontal className="w-4 h-4" />
        </Link>
      </header>

      {/* Snapshot card */}
      <div className="px-4 pt-6 pb-8">
        <div className="rounded-3xl overflow-hidden bg-white shadow-sm border border-stone-200" data-testid="preference-snapshot">
          {/* 3 image row */}
          <div className="grid grid-cols-3 gap-1 p-1">
            <div className="aspect-square rounded-2xl bg-stone-200 flex items-center justify-center">
              <p className="text-xl font-black text-stone-400">w:h</p>
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg,#f5d5b8,#d9a575)" }}>
              <div className="w-full h-full flex items-end justify-end p-1.5">
                <div className="w-3/4 h-3/4 rounded-lg bg-white/30" />
              </div>
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden ring-2 ring-sky-400" style={{ background: "linear-gradient(135deg,#e8b590,#a3735a)" }}>
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-2/3 h-2/3 rounded-full border-4 border-white/50" />
              </div>
            </div>
          </div>

          {/* Info row */}
          <div className="border-t border-stone-100 p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center font-black shrink-0">A</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-stone-900 truncate">Naomi Thomas</p>
              <p className="text-xs text-stone-500 truncate">Sister's wedding glam</p>
            </div>
            <Link to="/design/naomi-luxe?s=card-details" className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="card-eye"><Eye className="w-4 h-4 text-stone-700" /></Link>
          </div>

          {/* Footer chip */}
          <div className="border-t border-stone-100 px-3 py-2">
            <p className="text-[11px] text-stone-500 inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Jun 15</p>
          </div>
        </div>
        <p className="text-[10px] text-stone-400 text-center mt-3">Tap the eye to see all details</p>
      </div>

      <BottomNav current="card-owner" />
    </div>
    </div>
  );
}

// --- Preference Card DETAILS view (opened by eye icon) ---
function CardDetails() {
  return (
    <div className={PAGE}>
    <div className={FRAME}>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
        <Link to="/design/naomi-luxe?s=card-owner" className="w-8 h-8 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full" data-testid="details-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em]">Full details</p>
        <div className="w-8" />
      </header>

      <div className="pb-8">
        {/* Snapshot header - 3-image strip that IS the preference card snapshot */}
        <div className="px-5 pt-4">
          <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm">
            <div className="grid grid-cols-3 gap-0.5 bg-stone-200">
              {[
                "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=280&h=280&fit=crop&auto=format",
                "https://images.unsplash.com/photo-1596704017254-9b121068fb31?w=280&h=280&fit=crop&auto=format",
                "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=280&h=280&fit=crop&auto=format",
              ].map((src, i) => (
                <div
                  key={i}
                  className="aspect-square bg-stone-100"
                  style={{ backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
              ))}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-1.5">
                <Chip tone="amber">planning</Chip>
                <Chip tone="outline">Makeup</Chip>
              </div>
              <h1 className="text-xl font-black text-stone-900 mt-2">Sister's wedding glam</h1>
              <p className="text-xs text-stone-500 mt-1 inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Jun 15, 2026 - 10:30 AM</p>
              <p className="text-xs text-stone-500 mt-0.5">For Aria</p>
              <button className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-purple-700" data-testid="snapshot-pick-images">
                <Edit className="w-3 h-3" /> Owner: pick which 3 images show here
              </button>
              <p className="text-[10px] text-stone-500 mt-1 leading-snug">
                Tap 3-9 images until the service is done. After, you can flip them to before/after pairs. Share link auto-updates.
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
        {/* Full inspo grid (up to 12) */}
        <div className="rounded-2xl border border-stone-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-stone-900 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Inspiration (3 of 12)</p>
            <Chip tone="emerald" size="xs">Public</Chip>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {[
              "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=240&h=240&fit=crop&auto=format",
              "https://images.unsplash.com/photo-1596704017254-9b121068fb31?w=240&h=240&fit=crop&auto=format",
              "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=240&h=240&fit=crop&auto=format",
            ].map((src, i) => (
              <div
                key={i}
                className="aspect-square rounded-lg overflow-hidden bg-stone-100"
                style={{ backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center" }}
              />
            ))}
          </div>
        </div>

        {/* Preferences - visual tiles for anything that has imagery, swatch, or visual */}
        <div className="mt-3 rounded-2xl border border-stone-200 p-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold inline-flex items-center gap-1"><TagIcon className="w-3 h-3" /> Preferences</p>
            <Chip tone="stone" size="xs"><EyeOff className="w-2.5 h-2.5" /> My eyes only</Chip>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Skin type - text only (no visual defined) */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Skin type</p>
              <p className="text-xs font-semibold text-stone-900">Combination</p>
            </div>
            {/* Undertone - vein visual */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Undertone</p>
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1">
                <VeinHalf top="#65a30d" bottom="#a3a11c" />
                <span className="text-xs font-semibold text-stone-900">Warm</span>
              </div>
            </div>
            {/* Foundation shade - swatch */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Foundation</p>
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1">
                <div className="w-4 h-4 rounded-full border border-stone-300" style={{ backgroundColor: "#d9a575" }} />
                <div>
                  <p className="text-[11px] font-bold text-stone-900 leading-none">Fenty Pro Filt'r 240</p>
                </div>
              </div>
            </div>
            {/* Skin tone band */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Tone band</p>
              <div className="grid grid-cols-5 gap-0.5 max-w-[120px]">
                {["#f4dcc4","#e0b48a","#b98657","#8b5a35","#5a3620"].map((c, i) => (
                  <div key={i} className={`h-4 rounded-sm ${i === 2 ? "ring-2 ring-stone-900 ring-offset-1" : ""}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            {/* Eye shape - visual */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Eye shape</p>
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1">
                <TileVisual visualKey="eye-almond" />
                <span className="text-xs font-semibold text-stone-900">Almond</span>
              </div>
            </div>
            {/* Lash style - visual */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Lash style</p>
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1">
                <TileVisual visualKey="lash-volume" />
                <span className="text-xs font-semibold text-stone-900">Natural volume</span>
              </div>
            </div>
            {/* Eye look - 3 image thumbnails */}
            <div className="col-span-2">
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Eye look</p>
              <div className="flex items-center gap-2">
                <div className="grid grid-cols-3 gap-0.5">
                  {[
                    "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=80&h=80&fit=crop&auto=format",
                    "https://images.unsplash.com/photo-1503104834685-7205e8607eb9?w=80&h=80&fit=crop&auto=format",
                    "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&h=80&fit=crop&auto=format",
                  ].map((src, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 rounded"
                      style={{ backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center" }}
                    />
                  ))}
                </div>
                <span className="text-xs font-semibold text-stone-900">Bronze / natural</span>
              </div>
            </div>
            {/* Lip style - swatch */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Lip style</p>
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1">
                <div className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: "#d19999" }} />
                <span className="text-xs font-semibold text-stone-900">Ombre</span>
              </div>
            </div>
            {/* Lip finish - text */}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Lip finish</p>
              <p className="text-xs font-semibold text-stone-900">Matte</p>
            </div>
          </div>
        </div>

        {/* Products - photos + swatches */}
        <div className="mt-3 rounded-2xl border border-stone-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold inline-flex items-center gap-1"><TagIcon className="w-3 h-3" /> Products</p>
            <Chip tone="emerald" size="xs">Public</Chip>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { n: "Fenty 240", brand: "Fenty Beauty", img: "https://images.unsplash.com/photo-1631730359585-38a4935cbec4?w=240&h=240&fit=crop&auto=format", swatch: "#d9a575" },
              { n: "Pillow Talk", brand: "Charlotte Tilbury", img: "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=240&h=240&fit=crop&auto=format", swatch: "#c9787a" },
              { n: "Soft Glam", brand: "Anastasia BH", img: "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=240&h=240&fit=crop&auto=format", swatch: "#b8926d" },
            ].map(p => (
              <div key={p.n}>
                <div
                  className="aspect-square rounded-lg overflow-hidden bg-stone-100 relative"
                  style={{ backgroundImage: `url(${p.img})`, backgroundSize: "cover", backgroundPosition: "center" }}
                >
                  <div className="absolute bottom-1 left-1 w-4 h-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: p.swatch }} />
                </div>
                <p className="text-[10px] text-stone-900 font-semibold mt-1 truncate">{p.n}</p>
                <p className="text-[9px] text-stone-500 truncate">{p.brand}</p>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      <BottomNav current="card-details" />
    </div>
    </div>
  );
}
// --- Preference card - PUBLIC recipient view (page, not popup) ---
function CardPublic() {
  return (
    <div className={PAGE}>
    <div className={FRAME + " bg-stone-100"}>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
        <Link to="/design/naomi-luxe?s=card-owner" className="w-8 h-8 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full" data-testid="public-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <p className="text-[11px] font-bold text-stone-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Preference card - shared with you</p>
        <button className="w-8 h-8 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full"><MoreHorizontal className="w-4 h-4" /></button>
      </header>

      <div className="px-4 pt-6 pb-8">
        {/* Snapshot card */}
        <div className="rounded-3xl overflow-hidden bg-white shadow-sm border border-stone-200">
          <div className="grid grid-cols-3 gap-1 p-1">
            <div className="aspect-square rounded-2xl bg-stone-200 flex items-center justify-center">
              <p className="text-xl font-black text-stone-400">w:h</p>
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg,#f5d5b8,#d9a575)" }}>
              <div className="w-full h-full flex items-end justify-end p-1.5">
                <div className="w-3/4 h-3/4 rounded-lg bg-white/30" />
              </div>
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden ring-2 ring-sky-400" style={{ background: "linear-gradient(135deg,#e8b590,#a3735a)" }}>
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-2/3 h-2/3 rounded-full border-4 border-white/50" />
              </div>
            </div>
          </div>

          <div className="border-t border-stone-100 p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center font-black shrink-0">A</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-stone-900 truncate">Naomi Thomas</p>
              <p className="text-xs text-stone-500 truncate">Sister's wedding glam</p>
            </div>
            <button className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="eye-toggle"><Eye className="w-4 h-4 text-stone-700" /></button>
          </div>

          <div className="border-t border-stone-100 px-3 py-2">
            <p className="text-[11px] text-stone-500 inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Jun 15</p>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-5 space-y-2">
          <Btn tone="ink" full icon={Sparkles} size="md" testid="cta-plan-own">Plan your own experience</Btn>
          <button className="w-full rounded-full text-sm font-semibold py-3 inline-flex items-center justify-center gap-1.5 bg-purple-500 hover:bg-purple-600 text-white" data-testid="cta-join-phygital">
            <Heart className="w-4 h-4" /> Join Phygital
          </button>
          <Btn tone="outline" full icon={Sparkles} size="md" testid="cta-become-provider">Become a provider on Phygital</Btn>
        </div>
      </div>

      <BottomNav current="card-public" />
    </div>
    </div>
  );
}

// --- Visibility rules popup (V1: Public + My eyes only only, no host) ---
function CardVisibility() {
  const rows = [
    { field: "Preferences", pub: false, eyes: true },
    { field: "Inspiration", pub: true, eyes: false },
    { field: "Products", pub: true, eyes: false },
    { field: "Final look", pub: false, eyes: true },
    { field: "Allergies", pub: false, eyes: true },
    { field: "Address", pub: false, eyes: true },
    { field: "Phone", pub: false, eyes: true },
  ];
  return (
    <PopupShell current="card-visibility" title="Visibility settings" titleIcon={Zap} onClose="/design/naomi-luxe?s=dashboard-home" onBack="/design/naomi-luxe?s=card-owner"
      footer={<Link to="/design/naomi-luxe?s=card-owner"><Btn tone="ink" full icon={Save} size="sm" testid="save-visibility">Save visibility rules</Btn></Link>}
    >
      <div className="rounded-2xl border border-stone-200 bg-white">
        <div className="p-3 grid grid-cols-[1fr_auto_auto] gap-4 items-center text-[10px] uppercase tracking-wider text-stone-500 font-bold border-b border-stone-100">
          <span>Field</span><span>Public</span><span>My eyes only</span>
        </div>
        {rows.map(r => (
          <div key={r.field} className="p-3 grid grid-cols-[1fr_auto_auto] gap-4 items-center border-b border-stone-100 last:border-0">
            <p className="text-sm font-semibold text-stone-900">{r.field}</p>
            <VisDot on={r.pub} />
            <VisDot on={r.eyes} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-stone-500 mt-3 leading-relaxed"><b>My eyes only</b> = only you see it. <b>Public</b> = anyone with the share link. Host visibility unlocks with V3 bookings.</p>
    </PopupShell>
  );
}
const VisDot = ({ on }) => (
  <div className={`w-8 h-5 rounded-full flex items-center px-0.5 ${on ? "bg-stone-900 justify-end" : "bg-stone-200 justify-start"}`}>
    <div className="w-4 h-4 rounded-full bg-white" />
  </div>
);


// ============================================================================
// PHASE C - Preferences (single long scroll - matches Naomi's PDF spec)
// ============================================================================
const Swatch = ({ hex, selected }) => (
  <div className={`w-8 h-8 rounded-full ${selected ? "ring-2 ring-offset-2 ring-stone-900" : ""}`} style={{ backgroundColor: hex, boxShadow: "0 0 0 1px rgba(0,0,0,0.06)" }} />
);

// Reusable "tile card" used by Eye shape, Lash style, Lip style, Foundation shade, Favorite eye looks etc.
// visualKey lets us draw a small vector preview inside the tile (eye shape, lash style, etc.).
const TileVisual = ({ visualKey }) => {
  if (!visualKey) return null;
  // Eye shapes: pure CSS ovals + eyelid overlays.
  if (visualKey === "eye-almond") return (
    <svg viewBox="0 0 40 24" className="w-10 h-6"><ellipse cx="20" cy="12" rx="18" ry="8" fill="#f6f1e6" stroke="#292524" strokeWidth="1.5"/><circle cx="20" cy="12" r="4" fill="#292524"/></svg>
  );
  if (visualKey === "eye-round") return (
    <svg viewBox="0 0 40 28" className="w-10 h-6"><ellipse cx="20" cy="14" rx="14" ry="12" fill="#f6f1e6" stroke="#292524" strokeWidth="1.5"/><circle cx="20" cy="14" r="5" fill="#292524"/></svg>
  );
  if (visualKey === "eye-hooded") return (
    <svg viewBox="0 0 40 24" className="w-10 h-6"><ellipse cx="20" cy="14" rx="18" ry="8" fill="#f6f1e6" stroke="#292524" strokeWidth="1.5"/><path d="M2 12 Q20 3 38 12 L38 14 Q20 6 2 14 Z" fill="#c9b39a"/><circle cx="20" cy="14" r="4" fill="#292524"/></svg>
  );
  if (visualKey === "eye-monolid") return (
    <svg viewBox="0 0 40 22" className="w-10 h-6"><path d="M2 12 Q20 4 38 12 Q20 20 2 12 Z" fill="#f6f1e6" stroke="#292524" strokeWidth="1.5"/><circle cx="20" cy="12" r="4" fill="#292524"/></svg>
  );
  if (visualKey === "eye-downturned") return (
    <svg viewBox="0 0 40 22" className="w-10 h-6"><path d="M2 10 Q10 6 20 8 Q30 10 38 16 Q30 18 20 16 Q10 14 2 10 Z" fill="#f6f1e6" stroke="#292524" strokeWidth="1.5"/><circle cx="20" cy="12" r="4" fill="#292524"/></svg>
  );
  if (visualKey === "eye-upturned") return (
    <svg viewBox="0 0 40 22" className="w-10 h-6"><path d="M2 16 Q10 14 20 12 Q30 10 38 6 Q30 12 20 14 Q10 16 2 16 Z" fill="#f6f1e6" stroke="#292524" strokeWidth="1.5"/><circle cx="20" cy="12" r="4" fill="#292524"/></svg>
  );
  // Lash shapes: rendered as tiny lash sprays.
  if (visualKey === "lash-natural") return (
    <svg viewBox="0 0 44 24" className="w-10 h-6"><path d="M4 20 Q22 8 40 20" stroke="#292524" strokeWidth="1.6" fill="none"/>{[7,13,19,25,31,37].map(x=><line key={x} x1={x} y1={20 - (x>18&&x<28?7:5)} x2={x} y2="20" stroke="#292524" strokeWidth="1.2"/>)}</svg>
  );
  if (visualKey === "lash-wispy") return (
    <svg viewBox="0 0 44 24" className="w-10 h-6"><path d="M4 20 Q22 6 40 20" stroke="#292524" strokeWidth="1.6" fill="none"/>{[7,13,19,25,31,37].map((x,i)=><line key={x} x1={x} y1={20 - (i%2===0?10:5)} x2={x} y2="20" stroke="#292524" strokeWidth="1.2"/>)}</svg>
  );
  if (visualKey === "lash-volume") return (
    <svg viewBox="0 0 44 24" className="w-10 h-6"><path d="M4 20 Q22 4 40 20" stroke="#292524" strokeWidth="1.8" fill="none"/>{[6,10,14,18,22,26,30,34,38].map(x=><line key={x} x1={x} y1="10" x2={x} y2="20" stroke="#292524" strokeWidth="1.4"/>)}</svg>
  );
  if (visualKey === "lash-dramatic") return (
    <svg viewBox="0 0 44 24" className="w-10 h-6"><path d="M4 20 Q22 2 40 20" stroke="#292524" strokeWidth="1.8" fill="none"/>{[6,10,14,18,22,26,30,34,38].map((x,i)=><line key={x} x1={x} y1={20 - (i===4?14:i===3||i===5?12:9)} x2={x} y2="20" stroke="#292524" strokeWidth="1.5"/>)}</svg>
  );
  return null;
};

const OptionTile = ({ label, selected, hex, hint, visualKey }) => (
  <button className={`rounded-xl border-2 p-3 text-left transition ${selected ? "border-stone-900" : "border-stone-200 hover:border-stone-400"}`} style={{ backgroundColor: "#f6f1e6" }}>
    <div className="aspect-square rounded-lg bg-white/60 flex items-center justify-center mb-2">
      {visualKey ? <TileVisual visualKey={visualKey} /> : (hex && <div className="w-5 h-5 rounded-full border border-stone-300" style={{ backgroundColor: hex }} />)}
    </div>
    <p className="text-[11px] font-semibold text-stone-900 leading-tight">{label}</p>
    {hint && <p className="text-[9px] text-stone-500 mt-0.5">{hint}</p>}
  </button>
);

// LookLabel - a pill/label that also renders 3 inspo thumbnails so users
// can visually anchor "what does 'Bold color' actually look like?" without
// opening a modal. imgs accepts either CSS gradient strings OR image URLs
// (starts with "http"). Selected state adds a checkmark overlay + ring so
// the "before/after selection" states are obvious at a glance.
const LookLabel = ({ label, selected, imgs = [], autofillHint }) => {
  const renderTile = (val, i) => {
    const isUrl = typeof val === "string" && val.startsWith("http");
    return (
      <div
        key={i}
        className="aspect-square rounded-md overflow-hidden bg-stone-100"
        style={isUrl
          ? { backgroundImage: `url(${val})`, backgroundSize: "cover", backgroundPosition: "center" }
          : { background: val }
        }
      />
    );
  };
  return (
    <button className={`relative rounded-2xl border-2 p-2 flex flex-col gap-1.5 text-left transition ${selected ? "border-stone-900 bg-stone-900 text-white ring-2 ring-stone-900 ring-offset-2" : "border-stone-200 bg-white text-stone-900 hover:border-stone-400"}`}>
      {/* selected checkmark overlay */}
      {selected && (
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 flex items-center justify-center shadow">
          <Check className="w-3.5 h-3.5 text-stone-900" strokeWidth={3} />
        </span>
      )}
      <div className="grid grid-cols-3 gap-1">
        {(imgs.length ? imgs : ["#e5b8b0", "#c9b39a", "#8b5a35"]).map(renderTile)}
      </div>
      <p className="text-[11px] font-semibold leading-tight px-1">{label}</p>
      {selected && autofillHint && (
        <p className="text-[9px] px-1 text-white/80 leading-tight">
          <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
          {autofillHint}
        </p>
      )}
    </button>
  );
};

// Section anchor with grouping label ("Canvas", "Look", "Vibe")
const PrefGroup = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-16">
    <h2 className="text-lg font-black text-stone-900 tracking-tight">{title}</h2>
    <div className="mt-3 space-y-4">{children}</div>
  </section>
);

// PrefField now supports a `tooltip` prop that renders an Info icon with a
// hoverable title attribute. Every field can have a tooltip description sourced
// from `Naomi Pref Schema.Tooltip` in Airtable so admins can edit them without
// touching code. No em-dashes allowed in tooltip text (use hyphens instead).
const PrefField = ({ label, sub, tooltip, children }) => (
  <div>
    <p className="text-xs font-bold text-stone-900 inline-flex items-center gap-1">
      {label}
      {tooltip && (
        <span
          className="inline-flex items-center text-stone-400 hover:text-stone-700 cursor-help"
          title={tooltip}
          data-testid={`tooltip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
          <Info className="w-3 h-3" />
        </span>
      )}
    </p>
    {sub && <p className="text-[10px] text-stone-500 mt-0.5 leading-snug">{sub}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

// Collapsible section - closed by default. Header shows label + selected summary
// + chevron. Body slides open on tap. Used for Allergies and Accessibility so
// the Universal Defaults section stays compact when nothing has been set.
const PrefCollapsible = ({ label, tooltip, summary, defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-rose-200 bg-white overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-rose-50 transition"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-xs font-bold text-rose-900">{label}</p>
          {tooltip && (
            <span
              className="inline-flex items-center text-rose-400 hover:text-rose-700 cursor-help"
              title={tooltip}
            >
              <Info className="w-3 h-3" />
            </span>
          )}
          <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-500 ml-1 truncate">
            {summary || "None"}
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-rose-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-rose-100">
          {children}
        </div>
      )}
    </div>
  );
};

const Pill = ({ children, selected, tone = "cream" }) => {
  const tones = {
    cream: selected ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-800 border-stone-300",
    rose:  selected ? "bg-rose-100 text-rose-900 border-rose-300" : "bg-white text-stone-800 border-stone-300",
  };
  return <button className={`text-xs px-3 py-1.5 rounded-full border ${tones[tone]}`}>{children}</button>;
};

const LookupRow = ({ placeholder, autofilled, autofilledLabel }) => (
  <button className={`w-full rounded-xl border px-3 py-2.5 text-left text-xs flex items-center justify-between transition ${autofilled ? "border-stone-900 bg-stone-50 text-stone-900" : "border-stone-300 bg-white text-stone-500 hover:border-stone-500"}`}>
    <span className="flex items-center gap-1.5 min-w-0">
      {autofilled && (
        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-purple-700 bg-purple-100 border border-purple-200 rounded-full px-1.5 py-0.5 shrink-0">
          <Sparkles className="w-2.5 h-2.5" /> Auto
        </span>
      )}
      <span className="truncate">{autofilled ? autofilledLabel : placeholder}</span>
    </span>
    <ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />
  </button>
);

function PrefsAll() {
  return (
    <PopupShell
      current="preferences"
      title="Edit preferences"
      titleIcon={Edit}
      onClose="/design/naomi-luxe?s=dashboard-home"
      onBack="/design/naomi-luxe?s=ps-makeup-modal"
      footer={
        <div className="flex gap-2 items-center">
          <Link to="/design/naomi-luxe?s=ps-makeup-modal"><Btn tone="outline" size="sm">Cancel</Btn></Link>
          <Link
            to="/design/naomi-luxe?s=card-details"
            className="w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center hover:border-stone-900 flex-shrink-0"
            data-testid="prefs-eye-preview"
            title="Preview my preference card"
          >
            <Eye className="w-4 h-4 text-stone-700" />
          </Link>
          <Link to="/design/naomi-luxe?s=ps-makeup-modal" className="flex-1"><Btn tone="ink" full size="sm" icon={Save} testid="save-prefs">Save preferences</Btn></Link>
        </div>
      }
    >
      {/* Anchor jump nav within the popup */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {[
          { id: "universal", label: "Universal" },
          { id: "canvas", label: "Canvas" },
          { id: "look", label: "Look" },
          { id: "notes", label: "Notes" },
        ].map(a => (
          <a key={a.id} href={`#${a.id}`} className="text-[10px] font-bold uppercase tracking-wider text-stone-600 hover:text-stone-900 px-2.5 py-1 rounded-full bg-stone-50 border border-stone-200 whitespace-nowrap">
            {a.label}
          </a>
        ))}
      </div>

      <div className="space-y-6">
        {/* ============ UNIVERSAL DEFAULTS (Allergies + Accessibility) ============ */}
        <PrefGroup id="universal" title="Universal defaults">
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3 space-y-2">
            <div className="flex items-center gap-2 mb-1"><Lock className="w-3.5 h-3.5 text-rose-700" /><SectionLabel>Kept private on your public share</SectionLabel></div>
            <p className="text-[10px] text-rose-800 mb-2">Applies to every experience. Providers see these only when you allow it on the visibility screen.</p>

            <PrefCollapsible
              label="Allergies"
              tooltip="List anything a provider needs to plan around during a service. Kept private by default."
              summary="None"
              testid="universal-allergies"
            >
              <p className="text-[10px] text-rose-800 mb-2">Multiselect - tap all that apply. Leave empty for None.</p>
              <div className="flex flex-wrap gap-1.5">
                <Pill selected tone="rose">None</Pill>
                {["Latex", "Fragrance", "Nut oils", "Nickel", "Pollen", "Gluten (lip)", "Shellfish (glue)"].map((t) => (
                  <Pill key={t} tone="rose">{t}</Pill>
                ))}
              </div>
            </PrefCollapsible>

            <PrefCollapsible
              label="Accessibility"
              tooltip="Anything a provider should plan around: sensory, mobility, hearing, vision, or timing needs."
              summary="None"
              testid="universal-accessibility"
            >
              <p className="text-[10px] text-rose-800 mb-2">Multiselect - tap all that apply. Leave empty for None.</p>
              <div className="flex flex-wrap gap-1.5">
                <Pill selected tone="rose">None</Pill>
                {["Low fragrance", "Wheelchair accessible", "Sensory sensitive", "Hard of hearing", "Low vision", "Chair breaks"].map((t) => (
                  <Pill key={t} tone="rose">{t}</Pill>
                ))}
              </div>
            </PrefCollapsible>
          </div>
        </PrefGroup>

        {/* ============ CANVAS ============ */}
        <PrefGroup id="canvas" title="Canvas">
          <PrefField label="Skin type" sub="Multiselect - tap all that apply." tooltip="How your skin behaves day-to-day. Guides moisture prep, primer choice, and finish.">
            <div className="flex flex-wrap gap-1.5">
              {["Dry", "Oily", "Combo", "Sensitive", "Normal"].map((t, i) => <Pill key={t} selected={[0, 2].includes(i)}>{t}</Pill>)}
            </div>
          </PrefField>

          <PrefField label="Undertone" sub="Look at the veins on your wrist. Purple+blue = cool. Blue+green = neutral. Green+olive = warm." tooltip="Undertone drives foundation, bronzer, and lip color selection. Wrist-vein method shown below.">
            <div className="flex gap-2">
              {[
                { n: "Cool", top: "#7c3aed", bottom: "#3b82f6" },
                { n: "Neutral", top: "#3b82f6", bottom: "#16a34a" },
                { n: "Warm", top: "#65a30d", bottom: "#a3a11c" },
              ].map((u, i) => (
                <button key={u.n} className={`flex-1 rounded-xl border p-2.5 flex items-center gap-2 ${i === 2 ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 bg-white text-stone-800"}`}>
                  <VeinHalf top={u.top} bottom={u.bottom} />
                  <span className="text-xs font-semibold whitespace-nowrap">{u.n}</span>
                </button>
              ))}
            </div>
          </PrefField>

          <PrefField label="Skin concerns" tooltip="What you want addressed. Guides color-correcting steps and product selection.">
            <div className="flex flex-wrap gap-1.5">
              {["Acne", "Redness", "Dryness", "Oiliness", "Hyperpigmentation"].map((t, i) => <Pill key={t} selected={[0, 4].includes(i)}>{t}</Pill>)}
            </div>
          </PrefField>

          <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold text-stone-900 inline-flex items-center gap-1">
                Foundation shade
                <span className="inline-flex items-center text-stone-400 cursor-help" title="Your exact shade + product. Guides base color match so your provider stocks or brings the right SKU."><Info className="w-3 h-3" /></span>
              </p>
              <TypeTag>swatch</TypeTag>
            </div>
            <p className="text-[10px] text-stone-500 mb-3">Color swatch + product thumbnail + brand + shade code</p>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full" style={{ backgroundColor: "#d9a575" }} />
              <div className="w-9 h-9 rounded-lg bg-stone-200" />
              <div className="flex-1 ml-1">
                <p className="text-xs font-bold text-stone-900">Fenty Beauty</p>
                <p className="text-[11px] text-stone-500">Pro Filt'r 240</p>
              </div>
              <button className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Change</button>
            </div>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold text-stone-900 inline-flex items-center gap-1">
                Skin tone band
                <span className="inline-flex items-center text-stone-400 cursor-help" title="Broad-band tone bucket. Used as a fallback when a specific foundation SKU is unavailable."><Info className="w-3 h-3" /></span>
              </p>
              <TypeTag>tone-band</TypeTag>
            </div>
            <p className="text-[10px] text-stone-500 mb-3">5-band gradient with selected band highlighted</p>
            <div className="grid grid-cols-5 gap-1.5">
              {[
                { c: "#f4dcc4" },
                { c: "#e0b48a" },
                { c: "#b98657", sel: true },
                { c: "#8b5a35" },
                { c: "#5a3620" },
              ].map((s, i) => (
                <div key={i} className={`h-10 rounded-lg ${s.sel ? "ring-2 ring-stone-900 ring-offset-2" : ""}`} style={{ backgroundColor: s.c }} />
              ))}
            </div>
          </div>

          <PrefField label="Eye shape" tooltip="Guides how liner, shadow placement, and lash length are applied to flatter your natural shape.">
            <div className="grid grid-cols-3 gap-2">
              {[
                { l: "Almond", v: "eye-almond" },
                { l: "Round", v: "eye-round" },
                { l: "Hooded", v: "eye-hooded" },
                { l: "Monolid", v: "eye-monolid" },
                { l: "Downturned", v: "eye-downturned" },
                { l: "Upturned", v: "eye-upturned" },
              ].map((s, i) => (
                <OptionTile key={s.l} label={s.l} visualKey={s.v} selected={i === 0} />
              ))}
            </div>
          </PrefField>
        </PrefGroup>

        {/* ============ LOOK ============ */}
        <PrefGroup id="look" title="Look">
          <PrefField label="Select the Inspo for this look" tooltip="Add reference images so your provider knows the vibe. Upload from device or pick from your Inspo library.">
            <div className="bg-stone-100 p-1 rounded-full flex mb-3">
              <button className="flex-1 text-xs py-2 rounded-full bg-white shadow font-semibold text-stone-900 inline-flex items-center justify-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Upload</button>
              <button className="flex-1 text-xs py-2 rounded-full text-stone-500 inline-flex items-center justify-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> My Inspo (7)</button>
            </div>
            <div className="rounded-2xl border-2 border-dashed border-stone-300 bg-white p-8 text-center">
              <Camera className="w-6 h-6 mx-auto text-stone-400" />
              <p className="text-xs text-stone-500 mt-2">Tap to upload from device</p>
            </div>
          </PrefField>

          <PrefField label="Favorite eye looks" sub="Each label shows 3 example inspo thumbnails so you know what you're picking. Tap to auto-fill your palette + liner picks." tooltip="Picking a look here auto-suggests eyeshadow palettes and liner products below. You can override any pick.">
            <div className="grid grid-cols-2 gap-2">
              {[
                { l: "Bold color", i: [
                  "https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1583241800698-e8ab01830a07?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1620015101613-a3ba03afb043?w=180&h=180&fit=crop&auto=format",
                ], hint: "Auto-fills UD Electric palette + colored liner" },
                { l: "Smokey", i: [
                  "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1596704017254-9b121068fb31?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=180&h=180&fit=crop&auto=format",
                ], hint: "Auto-fills Naked Smoky + black liner" },
                { l: "Glitter", i: [
                  "https://images.unsplash.com/photo-1595475207225-428b62bda831?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1596704017254-9b121068fb31?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=180&h=180&fit=crop&auto=format",
                ], hint: "Auto-fills Stila Glitter & Glow + gel liner" },
                { l: "Bronze / natural", i: [
                  "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1503104834685-7205e8607eb9?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=180&h=180&fit=crop&auto=format",
                ], hint: "Auto-fills Soft Glam palette + brown liner" },
                { l: "Heavy eyeshadow", i: [
                  "https://images.unsplash.com/photo-1583241800698-e8ab01830a07?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1620015101613-a3ba03afb043?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=180&h=180&fit=crop&auto=format",
                ], hint: "Auto-fills Modern Renaissance + winged liner" },
                { l: "No eyeshadow", i: [
                  "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1503104834685-7205e8607eb9?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=180&h=180&fit=crop&auto=format",
                ], hint: "Skips palette; keeps brow + mascara" },
                { l: "Winged liner", i: [
                  "https://images.unsplash.com/photo-1620015101613-a3ba03afb043?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1583241800698-e8ab01830a07?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=180&h=180&fit=crop&auto=format",
                ], hint: "Auto-fills Kat Von D Tattoo + soft neutral" },
                { l: "No liner", i: [
                  "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1503104834685-7205e8607eb9?w=180&h=180&fit=crop&auto=format",
                  "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=180&h=180&fit=crop&auto=format",
                ], hint: "Skips liner; keeps lash + mascara" },
              ].map((l, i) => (
                <LookLabel key={l.l} label={l.l} selected={[0, 3].includes(i)} imgs={l.i} autofillHint={l.hint} />
              ))}
            </div>
          </PrefField>

          <PrefField label="Favorite eyeshadow palettes" tooltip="Auto-filled based on your eye look choice above. Tap to browse the full catalog.">
            <LookupRow placeholder="Tap to pick products" autofilled autofilledLabel="UD Electric + Naked Smoky" />
          </PrefField>

          <PrefField label="Favorite eyeliner" tooltip="Auto-filled based on your eye look choice above. Long-wearing gel liners tend to hold best under humidity.">
            <LookupRow placeholder="Tap to pick products" autofilled autofilledLabel="Kat Von D Tattoo Liner - Trooper Black" />
          </PrefField>

          <PrefField label="Lash style" tooltip="From natural to dramatic. Guides strip choice, mapping, or extensions if you go pro.">
            <div className="grid grid-cols-4 gap-2">
              {[
                { l: "Natural", v: "lash-natural" },
                { l: "Wispy", v: "lash-wispy" },
                { l: "Volume", v: "lash-volume" },
                { l: "Dramatic", v: "lash-dramatic" },
              ].map((s, i) => (
                <OptionTile key={s.l} label={s.l} visualKey={s.v} selected={i === 1} />
              ))}
            </div>
          </PrefField>

          <PrefField label="Lash strip brand" tooltip="Save the strip brands and styles you love so your provider can match them.">
            <LookupRow placeholder="Tap to pick products" />
          </PrefField>

          <PrefField label="Lip style" tooltip="How your lip is drawn. Ombre and dramatic liners give more definition; natural stays subtle.">
            <div className="grid grid-cols-3 gap-2">
              {[
                { l: "Natural - sheer to no lip", c: "#e5b8b0" },
                { l: "Ombre effect", c: "#d19999" },
                { l: "Deep dramatic lip liner", c: "#a55b58" },
              ].map((lp, i) => (
                <button key={lp.l} className={`rounded-xl border-2 p-3 bg-white ${i === 1 ? "border-stone-900" : "border-stone-200"}`}>
                  <div className="aspect-square rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: "#fce7e5" }}>
                    <div className="w-6 h-6 rounded-full border border-white" style={{ backgroundColor: lp.c }} />
                  </div>
                  <p className="text-[11px] font-semibold text-stone-900 leading-tight">{lp.l}</p>
                </button>
              ))}
            </div>
          </PrefField>

          <PrefField label="Lip finish" tooltip="Matte reads bolder; gloss reads softer and reflective.">
            <div className="flex gap-2">
              <Pill selected>Matte</Pill>
              <Pill>Gloss</Pill>
            </div>
          </PrefField>

          <PrefField label="Favorite lip products" tooltip="Save the lip colors, liners, and glosses you already love so your provider stocks them.">
            <LookupRow placeholder="Tap to pick products" />
          </PrefField>

          <PrefField label="Tones you like on your skin" tooltip="Guides bronzer, highlighter, and eyeshadow warmth choices.">
            <div className="flex gap-2 flex-wrap">
              {[
                { n: "Gold", c: "#d4af37" }, { n: "Silver", c: "#c0c0c0" }, { n: "Both", c: "linear-gradient(90deg,#d4af37,#c0c0c0)" }, { n: "No preference", c: "#f5f5f4" },
              ].map((t, i) => (
                <button key={t.n} className={`rounded-full border px-3 py-1.5 inline-flex items-center gap-2 ${i === 0 ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 bg-white"}`}>
                  <div className="w-4 h-4 rounded-full" style={{ background: t.c }} />
                  <span className="text-xs font-semibold">{t.n}</span>
                </button>
              ))}
            </div>
          </PrefField>

          <div className="grid grid-cols-2 gap-4">
            <PrefField label="Finish" tooltip="Matte holds longer under lights; dewy reads more youthful and reflective.">
              <div className="flex gap-2">
                <Pill>Matte</Pill>
                <Pill selected>Dewy</Pill>
              </div>
            </PrefField>
            <PrefField label="Setting spray of choice" tooltip="Save the setting sprays you trust for humidity or long wear.">
              <LookupRow placeholder="Tap to pick products" />
            </PrefField>
          </div>

          <PrefField label="Favorite brands" tooltip="Save the brands you love so your provider steers toward them when picking products.">
            <LookupRow placeholder="Tap to pick brands" />
          </PrefField>

          <PrefField label="Favorite products" tooltip="Any specific SKUs you always want used. Overrides brand-level picks.">
            <LookupRow placeholder="Tap to pick products" />
          </PrefField>
        </PrefGroup>

        {/* ============ NOTES ============ */}
        <PrefGroup id="notes" title="Notes">
          <PrefField label="Anything else your MUA should know" tooltip="Free-form notes. Great for pregnancy status, prior reactions, or timing constraints.">
            <textarea placeholder="Any notes" rows={4} className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2.5 text-xs focus:outline-none focus:border-stone-900 resize-none" />
          </PrefField>
        </PrefGroup>
      </div>
    </PopupShell>
  );
}

// Half-circle vein swatch: two colors representing the vein tones visible on the wrist
const VeinHalf = ({ top, bottom }) => (
  <div className="relative w-6 h-6 rounded-full overflow-hidden border border-stone-300 shrink-0">
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1" style={{ backgroundColor: top }} />
      <div className="flex-1" style={{ backgroundColor: bottom }} />
    </div>
  </div>
);

// Small chip that labels the input type (swatch, tone-band, pill-multi, etc.)
const TypeTag = ({ children }) => (
  <span className="text-[9px] font-bold uppercase tracking-wider bg-stone-100 text-stone-600 rounded-full px-2 py-0.5 border border-stone-200">{children}</span>
);


function PdAddNew() {
  return (
    <PopupShell
      current="add-provider"
      title="Add new provider"
      titleIcon={Plus}
      onClose="/design/naomi-luxe?s=dashboard-home"
      onBack="/design/naomi-luxe?s=ps-makeup-providers-open"
      footer={<Link to="/design/naomi-luxe?s=ps-makeup-provider-selected"><Btn tone="ink" full icon={Save} size="sm" testid="save-provider">Save provider</Btn></Link>}
    >
      <div className="space-y-3">
        <div>
          <FieldLabel>Name</FieldLabel>
          <TextInput placeholder="Jane Smith" testid="provider-name" />
        </div>
        <div>
          <FieldLabel>Brand / studio</FieldLabel>
          <TextInput placeholder="Jane's Beauty Bar" testid="provider-brand" />
        </div>
        <div>
          <FieldLabel>Category</FieldLabel>
          <div className="flex gap-1.5 flex-wrap mt-1">
            {PILOT_CATEGORIES.map((c, i) => (
              <button key={c.name} className={`text-[11px] px-2.5 py-1 rounded-full border ${i === 0 ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600"}`}>{c.name}</button>
            ))}
          </div>
        </div>
        <div>
          <FieldLabel>Studio address</FieldLabel>
          <TextInput placeholder="1947 N. Cahuenga Blvd, Los Angeles, CA" testid="provider-address" icon={MapPin} />
        </div>
        <div>
          <FieldLabel>Phone (optional)</FieldLabel>
          <TextInput placeholder="(213) 555 0000" testid="provider-phone" icon={Phone} />
        </div>
        <div>
          <FieldLabel>Instagram (optional)</FieldLabel>
          <TextInput placeholder="@handle" testid="provider-ig" />
        </div>
      </div>
    </PopupShell>
  );
}



// ============================================================================
// ADMIN - Control Center (single long scroll)
// Static preview of every field an admin can edit for V1.
// See /app/memory/NAOMI_LUXE_DATA_MODEL.md for storage mapping.
// ============================================================================
const AC_Section = ({ id, title, sub, children }) => (
  <section id={id} className="scroll-mt-16 border-b border-stone-200 pb-8 mb-8">
    <div className="flex items-baseline gap-3 mb-1">
      <h2 className="text-xl font-black text-stone-900 tracking-tight">{title}</h2>
      <a href={`#${id}`} className="text-[10px] text-stone-400 font-mono">#{id}</a>
    </div>
    {sub && <p className="text-xs text-stone-500 mb-4 leading-relaxed max-w-xl">{sub}</p>}
    {children}
  </section>
);

const AC_Row = ({ label, value, source, edit }) => (
  <div className="grid grid-cols-[1fr_auto] gap-2 items-start py-2 border-b border-stone-100 last:border-0">
    <div className="min-w-0">
      <p className="text-xs font-semibold text-stone-900">{label}</p>
      <p className="text-[11px] text-stone-500 mt-0.5 break-words">{value}</p>
      {source && <p className="text-[9px] text-stone-400 font-mono mt-1">{source}</p>}
    </div>
    <button className="text-[10px] font-bold uppercase tracking-wider text-purple-700 hover:text-purple-900 shrink-0">{edit || "Edit"}</button>
  </div>
);

const AC_CategoryEditor = ({ name }) => {
  const c = CATEGORY_COLORS[name];
  const Icon = c?.Icon;
  return (
    <div className="rounded-2xl border border-stone-200 p-3 bg-white">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: c.soft }}>
          {Icon && <Icon className="w-6 h-6" style={{ color: c.hex }} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-stone-900">{name}</p>
          <div className="flex gap-1 mt-1 items-center">
            <span className="text-[10px] font-mono text-stone-500">{c.hex}</span>
            <span className="text-[10px] font-mono text-stone-300">/</span>
            <span className="text-[10px] font-mono text-stone-500">{c.soft}</span>
          </div>
        </div>
      </div>
      {/* Media library upload */}
      <button className="mt-3 w-full rounded-xl border border-dashed border-stone-300 py-2 text-[10px] font-bold uppercase tracking-wider text-stone-700 inline-flex items-center justify-center gap-1 hover:border-stone-900" data-testid={`upload-${name.toLowerCase()}`}>
        <Upload className="w-3 h-3" /> Upload icon
      </button>
      <p className="text-[9px] text-stone-400 font-mono mt-2">phygital/naomi-luxe/categories/{name.toLowerCase()}.svg</p>
    </div>
  );
};

function AdminControlCenter() {
  const sections = [
    { id: "brand", label: "Brand" },
    { id: "categories", label: "Categories" },
    { id: "plan-modal", label: "Plan Modal Copy" },
    { id: "preferences", label: "Preferences Fields" },
    { id: "occasions", label: "Occasions" },
    { id: "providers", label: "Providers Schema" },
    { id: "ugc", label: "UGC Storage" },
    { id: "features", label: "Feature Flags" },
  ];
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-30 bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/design/naomi-luxe" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Control Center - V1</span>
          </Link>
          <div className="flex items-center gap-2">
            <Chip tone="outline">Static preview</Chip>
            <Chip tone="ink">Airtable + Mongo</Chip>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-2 flex gap-1.5 overflow-x-auto border-t border-stone-100">
          {sections.map(s => (
            <a key={s.id} href={`#${s.id}`} className="text-[10px] font-bold uppercase tracking-wider text-stone-600 hover:text-stone-900 px-2.5 py-1 rounded-full bg-stone-50 border border-stone-200 whitespace-nowrap">
              {s.label}
            </a>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-black text-stone-900 tracking-tight">Naomi Luxe - Control Center</h1>
        <p className="text-sm text-stone-600 mt-2 max-w-2xl leading-relaxed">Every label, color, icon, dropdown and preference field is editable from this screen. Nothing is hardcoded in the mockup. Fields are stored in Airtable CMS (settings + catalogs) or Mongo (user-generated content).</p>

        {/* Airtable connection card */}
        <div className="mt-5 rounded-2xl border border-stone-900 bg-stone-900 text-white p-4 flex items-center gap-4 max-w-3xl">
          <div className="w-10 h-10 rounded-xl bg-yellow-400 flex items-center justify-center text-stone-900 font-black">A</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider">Connected Airtable base</p>
            <p className="text-sm font-mono truncate mt-0.5" data-testid="airtable-base-id">phygital_cms::naomi_luxe_v1 (base app8kEXAMPLE)</p>
            <p className="text-[10px] text-white/60 mt-1">Tables synced: brand, categories, copy, pref_schema, pref_option, occasions, providers_schema, features. Last pull: 2 min ago.</p>
          </div>
          <button className="text-[10px] font-bold uppercase tracking-wider bg-white text-stone-900 rounded-full px-3 py-1.5 inline-flex items-center gap-1" data-testid="sync-airtable"><RefreshCw className="w-3 h-3" /> Sync now</button>
        </div>

        {/* Media library card */}
        <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-4 flex items-center gap-4 max-w-3xl">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center text-sky-700 font-black">M</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-stone-900">Media library</p>
            <p className="text-sm font-mono truncate mt-0.5 text-stone-800" data-testid="media-folder">phygital/naomi-luxe/*</p>
            <p className="text-[10px] text-stone-500 mt-1">All category icons, option tile art, and inspo images are stored here. Upload from any admin row - no bundled assets.</p>
          </div>
          <button className="text-[10px] font-bold uppercase tracking-wider bg-stone-900 text-white rounded-full px-3 py-1.5 inline-flex items-center gap-1" data-testid="open-media-library"><Upload className="w-3 h-3" /> Open library</button>
        </div>

        {/* Brand */}
        <div className="mt-10" />
        <AC_Section id="brand" title="Brand" sub="Global look and feel for the entire /design/naomi-luxe mockup.">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-stone-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-900">Product name</p>
              <input defaultValue="phygital" className="w-full mt-2 text-xl font-black text-stone-900 border-b border-stone-200 focus:outline-none focus:border-stone-900" />
              <p className="text-[10px] text-stone-400 font-mono mt-2">airtable::brand.product_name</p>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-900">Accent color</p>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-8 h-8 rounded-full bg-purple-600" />
                <input defaultValue="#8b5cf6" className="text-sm font-mono border-b border-stone-200 focus:outline-none focus:border-stone-900" />
              </div>
              <p className="text-[10px] text-stone-400 font-mono mt-2">airtable::brand.accent_hex</p>
            </div>
          </div>
        </AC_Section>

        {/* Categories */}
        <AC_Section id="categories" title="Categories" sub="Icons, labels, and pastel color pairs for the 6 experience types shown on the Category picker modal.">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {PILOT_CATEGORIES.map(c => <AC_CategoryEditor key={c.name} name={c.name} />)}
          </div>
          <p className="text-[10px] text-stone-400 font-mono mt-3">airtable::categories.* (id, name, hex, soft_hex, icon_svg, description, sort_order)</p>
        </AC_Section>

        {/* Plan Modal Copy */}
        <AC_Section id="plan-modal" title="Plan Modal Copy" sub="Every visible label, hint, and button inside the Plan Makeup Experience popup.">
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            {[
              ["Modal title", "Plan Your Makeup Experience", "airtable::copy.plan_modal.title"],
              ["Back link label", "Back", "airtable::copy.plan_modal.back"],
              ["Category chip edit tooltip", "Change category", "airtable::copy.plan_modal.change_category"],
              ["Section label - Who", "Who is doing it?", "airtable::copy.plan_modal.who_label"],
              ["Who option 1", "DIY", "airtable::copy.plan_modal.who.diy"],
              ["Who option 2", "TBD", "airtable::copy.plan_modal.who.tbd"],
              ["Who option 3 (default)", "Add Provider", "airtable::copy.plan_modal.who.add"],
              ["My Providers dropdown label", "My Providers ({count})", "airtable::copy.plan_modal.who.my_providers"],
              ["Section label - Where", "Where?", "airtable::copy.plan_modal.where_label"],
              ["Where option 1", "TBD - just city", "airtable::copy.plan_modal.where.tbd"],
              ["Where option 2", "At provider", "airtable::copy.plan_modal.where.at_provider"],
              ["Where option 3", "Travel to me", "airtable::copy.plan_modal.where.travel"],
              ["City hint", "Just the city, not a full address. We'll narrow it down with your host.", "airtable::copy.plan_modal.where.city_hint"],
              ["Section label - When", "When?", "airtable::copy.plan_modal.when_label"],
              ["When option 1", "Date TBD", "airtable::copy.plan_modal.when.tbd"],
              ["When option 2", "Pick date & time", "airtable::copy.plan_modal.when.pick"],
              ["Section label - How much", "How much?", "airtable::copy.plan_modal.how_much_label"],
              ["How much helper", "Auto populates from your provider. Editable once your host confirms.", "airtable::copy.plan_modal.how_much_hint"],
              ["Occasion label", "What's the occasion? (optional)", "airtable::copy.plan_modal.occasion_label"],
              ["Occasion placeholder", "Search or pick an occasion...", "airtable::copy.plan_modal.occasion_placeholder"],
              ["Preferences shortcut", "Edit preferences for this look", "airtable::copy.plan_modal.prefs_cta"],
              ["Inspo shortcut", "Add inspo (upload or from library)", "airtable::copy.plan_modal.inspo_cta"],
              ["Primary save button", "Save experience", "airtable::copy.plan_modal.save"],
              ["Cancel button", "Cancel", "airtable::copy.plan_modal.cancel"],
            ].map(([l, v, s]) => <AC_Row key={l} label={l} value={v} source={s} />)}
          </div>
        </AC_Section>

        {/* Preferences Fields */}
        <AC_Section id="preferences" title="Preferences Fields" sub="Every field on the Edit preferences popup. All Pill groups are multiselect. Live preview below - matches what appears in the app.">
          <div className="space-y-3">
            {[
              { label: "Skin type", type: "pill_multi", opts: ["Dry", "Oily", "Combo", "Sensitive", "Normal"], sel: [0, 2], src: "airtable::pref_field.skin_type" },
              { label: "Skin concerns", type: "pill_multi", opts: ["Acne", "Redness", "Dryness", "Oiliness", "Hyperpigmentation"], sel: [0, 4], src: "airtable::pref_field.skin_concerns" },
              { label: "Favorite eye looks", type: "pill_multi", opts: ["Bold color", "Smokey", "Glitter", "Bronze / natural", "Winged liner"], sel: [0, 3], src: "airtable::pref_field.eye_looks" },
              { label: "Lash style", type: "tile", opts: ["Natural", "Wispy", "Volume", "Dramatic"], sel: [1], src: "airtable::pref_field.lash_style" },
              { label: "Eye shape", type: "tile", opts: ["Almond", "Round", "Hooded", "Monolid", "Down", "Up"], sel: [0], src: "airtable::pref_field.eye_shape" },
              { label: "Allergies", type: "pill_rose", opts: ["Latex", "Fragrance", "Nut oils", "Nickel", "Pollen"], sel: [0, 1], src: "airtable::pref_field.allergies" },
              { label: "Lip finish", type: "pill_multi", opts: ["Matte", "Gloss"], sel: [0], src: "airtable::pref_field.lip_finish" },
              { label: "Skin finish", type: "pill_multi", opts: ["Matte", "Dewy"], sel: [1], src: "airtable::pref_field.finish" },
            ].map(f => (
              <div key={f.label} className="rounded-2xl border border-stone-200 bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-bold text-stone-900">{f.label}</p>
                    <p className="text-[10px] text-stone-500">{f.type.startsWith("pill") ? "Multiselect" : "Single-select"} - {f.type === "tile" ? "Tile group" : f.type === "pill_rose" ? "Rose pills (private)" : "Pills"}</p>
                  </div>
                  <button className="text-[10px] font-bold uppercase text-purple-700">Edit</button>
                </div>
                {f.type === "tile" ? (
                  <div className="grid grid-cols-4 gap-2">
                    {f.opts.map((o, i) => <OptionTile key={o} label={o} selected={f.sel.includes(i)} />)}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {f.opts.map((o, i) => <Pill key={o} selected={f.sel.includes(i)} tone={f.type === "pill_rose" ? "rose" : "cream"}>{o}</Pill>)}
                  </div>
                )}
                <p className="text-[10px] text-stone-400 font-mono mt-3">{f.src}</p>
              </div>
            ))}
            <div className="rounded-2xl border border-dashed border-stone-300 p-4 text-center">
              <button className="text-xs font-bold text-purple-700 uppercase tracking-wider inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add preference field</button>
              <p className="text-[10px] text-stone-500 mt-1">Appends a row to airtable::pref_schema and shows automatically in the app.</p>
            </div>
          </div>
        </AC_Section>

        {/* Occasions */}
        <AC_Section id="occasions" title="Occasions" sub="Dropdown values inside 'What's the occasion?'. Multiselect for the user - all that apply.">
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {["Wedding", "Photoshoot", "Party", "Vacation", "Everyday", "Date night", "Birthday", "Girls trip"].map((o, i) => (
                <Pill key={o} selected={[0, 3].includes(i)}>{o}</Pill>
              ))}
            </div>
            <p className="text-[10px] text-stone-400 font-mono">airtable::occasions.* (id, name, sub_events, default_services, sort_order, enabled)</p>
            <div className="pt-3 border-t border-stone-100 mt-3">
              <button className="text-[11px] font-bold text-purple-700 uppercase tracking-wider inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add occasion</button>
            </div>
          </div>
        </AC_Section>

        {/* Providers */}
        <AC_Section id="providers" title="Providers Schema" sub="Every field the 'Add new provider' popup collects. Also the shape of what appears in the Who dropdown.">
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            {[
              ["name", "Text - required", "airtable::providers.name"],
              ["brand", "Text - optional", "airtable::providers.brand"],
              ["category", "Single-select -> categories.id", "airtable::providers.category"],
              ["address", "Text - required if 'At provider'", "airtable::providers.address"],
              ["phone", "Phone - optional", "airtable::providers.phone"],
              ["instagram", "Text - optional", "airtable::providers.instagram"],
              ["price_range_low", "Number - USD", "airtable::providers.price_low"],
              ["price_range_high", "Number - USD", "airtable::providers.price_high"],
              ["owner_user_id", "Ref -> users.id (private per user)", "mongo::users.providers"],
            ].map(([l, v, s]) => <AC_Row key={l} label={l} value={v} source={s} />)}
          </div>
        </AC_Section>

        {/* UGC */}
        <AC_Section id="ugc" title="UGC Storage" sub="Where user-generated content lives. Airtable is the source of truth for catalogs/settings; Mongo stores every user record.">
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            {[
              ["Saved experiences (plan makeup entries)", "One doc per experience", "mongo::user_experiences"],
              ["Preference selections", "One doc per user, updated in place", "mongo::user_preferences"],
              ["Inspo uploads", "Image files in media library, refs in Mongo", "medialib + mongo::user_inspo"],
              ["Providers (private per user)", "Nested array on user, mirrored to Airtable optionally", "mongo::users.providers"],
              ["Notes for MUA", "Free text field on user_experiences", "mongo::user_experiences.notes"],
              ["Occasion selection", "Ref to airtable::occasions.id on experience", "mongo::user_experiences.occasion_id"],
              ["Provider auto-populate price", "Read-through from providers.price_range on save", "mongo::user_experiences.price_estimate"],
            ].map(([l, v, s]) => <AC_Row key={l} label={l} value={v} source={s} />)}
          </div>
        </AC_Section>

        {/* Feature Flags */}
        <AC_Section id="features" title="Feature Flags" sub="What is live in V1. Anything flipped off never renders. See FEATURE_MAP.md for the roadmap.">
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            {[
              ["plan_service_modal", "ON - V1 core", "airtable::features.plan_service_modal"],
              ["plan_occasion_modal", "OFF - v2", "airtable::features.plan_occasion_modal"],
              ["preferences_long_scroll", "ON - V1", "airtable::features.preferences_long_scroll"],
              ["provider_directory", "ON - private per user", "airtable::features.provider_directory"],
              ["inspo_pinterest", "OFF - later", "airtable::features.inspo_pinterest"],
              ["inspo_dm_capture", "OFF - later", "airtable::features.inspo_dm_capture"],
              ["host_bookings", "OFF - not until dedicated release", "airtable::features.host_bookings"],
              ["acuity_sync", "OFF - not in V1", "airtable::features.acuity_sync"],
              ["vibe_section", "OFF - deferred", "airtable::features.vibe_section"],
              ["kanban_client_directory", "OFF - admin only later", "airtable::features.kanban_client_directory"],
            ].map(([l, v, s]) => (
              <div key={l} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center py-2 border-b border-stone-100 last:border-0">
                <p className="text-xs font-semibold text-stone-900">{l}</p>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${v.startsWith("ON") ? "text-emerald-700" : "text-stone-400"}`}>{v}</span>
                <p className="text-[9px] text-stone-400 font-mono">{s}</p>
              </div>
            ))}
          </div>
        </AC_Section>

        <footer className="mt-10 pt-6 border-t border-stone-200 text-[11px] text-stone-500 leading-relaxed">
          <p><b className="text-stone-700">Docs:</b> Components live in <code className="bg-stone-100 px-1.5 py-0.5 rounded">NAOMI_LUXE_COMPONENTS.md</code>. Data mapping in <code className="bg-stone-100 px-1.5 py-0.5 rounded">NAOMI_LUXE_DATA_MODEL.md</code>. Feature roadmap in <code className="bg-stone-100 px-1.5 py-0.5 rounded">NAOMI_LUXE_FEATURE_MAP.md</code>.</p>
        </footer>
      </div>
      <BottomNav current="admin-control" />
    </div>
  );
}

// ============================================================================
// ROUTER
// ============================================================================
export default function NaomiLuxePlanService() {
  const [params] = useSearchParams();
  const s = params.get("s");

  if (!s) return <NaomiLuxeIndex />;

  switch (s) {
    case "dashboard-home": return <DashboardHome />;

    case "ps-category": return <PsCategory />;
    case "ps-makeup-modal": return <PsMakeupModal variant="tbd" />;
    case "ps-makeup-providers-open": return <PsMakeupModal variant="providers-open" />;
    case "ps-makeup-provider-selected": return <PsMakeupModal variant="provider-selected" />;
    case "ps-makeup-pick-datetime": return <PsMakeupModal variant="pick-datetime" />;

    case "preferences": return <PrefsAll />;
    case "inspo": return <PsInspo />;
    case "add-provider": return <PdAddNew />;

    case "card-owner": return <CardOwner />;
    case "card-details": return <CardDetails />;
    case "card-public": return <CardPublic />;
    case "card-visibility": return <CardVisibility />;

    case "admin-control": return <AdminControlCenter />;

    default: return <NaomiLuxeIndex />;
  }
}
